import cadviewer from "cadviewer";
import { NewSearchResult } from "../types/aiChatClient";
import { RelocationData, RelocationSpaceDetail } from "../types/aiChatClientV5";
import { safeJSONParse } from "./jsonUtils";

const normalizeSpaceId = (raw: string): string =>
  raw?.replaceAll?.(".", "_").replaceAll?.(" ", "_") || raw;

export const isVacantTenant = (tenant?: string | null): boolean =>
  !!tenant && tenant.trim().toLowerCase() === "vacant";

// Styles for highlighting spaces in CADViewer

export const highlight_blue = {
  fill: "#0c8dff",
  "fill-opacity": 0.8,
  stroke: "#003366", // midnight blue 003366 ,    #0c8dff
  "stroke-width": 2.0,
  "stroke-opacity": 1.0,
  "stroke-linejoin": "round",
};

export const highlight_yellow = {
  fill: "#fafa00", //  #FFFCBB yellow
  "fill-opacity": 0.8,
  stroke: "#FFD300", //  orange
  "stroke-width": 2.0,
  "stroke-opacity": 1,
  "stroke-linejoin": "round",
};

export const highlight_purple = {
  fill: "#ff00dd",
  "fill-opacity": 0.8,
  stroke: "#800080", //  purple          #ff00dd
  "stroke-width": 2.0,
  "stroke-opacity": 1,
  "stroke-linejoin": "round",
};
export const highlight_red = {
  fill: "#fa8072",
  "fill-opacity": 0.8,
  stroke: "#8B0000",
  "stroke-width": 2.0,
  "stroke-opacity": 1,
  "stroke-linejoin": "round",
};

export const highlight_green = {
  fill: "#32CD32",
  "fill-opacity": 0.8,
  stroke: "#228B22",
  "stroke-width": 2.0,
  "stroke-opacity": 1,
  "stroke-linejoin": "round",
};

/**
 * Create a CADViewer highlight style object from a hex color string.
 * Darkens the fill color by 30% to generate the stroke color automatically.
 */
export function createHighlightStyle(hexColor: string) {
  // Darken the color for stroke by reducing each RGB channel by ~30%
  const darken = (hex: string): string => {
    const r = Math.max(0, Math.round(parseInt(hex.slice(1, 3), 16) * 0.7));
    const g = Math.max(0, Math.round(parseInt(hex.slice(3, 5), 16) * 0.7));
    const b = Math.max(0, Math.round(parseInt(hex.slice(5, 7), 16) * 0.7));
    return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
  };

  return {
    fill: hexColor,
    "fill-opacity": 0.8,
    stroke: darken(hexColor),
    "stroke-width": 2.0,
    "stroke-opacity": 1,
    "stroke-linejoin": "round",
  };
}

/**
 * Highlight spaces in CADViewer based on search results
 *
 * @param searchResult The search result containing target and found spaces
 * @param clearExisting Whether to clear existing highlights before applying new ones
 */
export function highlightSearchResults(
  searchResult: NewSearchResult,
  clearExisting: boolean = true
): void {
  //console.log("highlightSearchResults - ", searchResult, clearExisting);
  if (clearExisting) {
    cadviewer.cvjs_clearSpaceLayer();
  }

  // Highlight target space(s) in red (or custom highlight_color if provided)
  // Support both single targetPlanInfo and multiple targetPlanInfos
  const resultWithMultipleTargets = searchResult as any;

  if (resultWithMultipleTargets.targetPlanInfos && Array.isArray(resultWithMultipleTargets.targetPlanInfos)) {
    // Highlight all targets from the array
    resultWithMultipleTargets.targetPlanInfos.forEach((targetInfo: any) => {
      if (targetInfo?.spaceNumber) {
        const spaceNumber = targetInfo.spaceNumber.replaceAll?.(".", "_").replaceAll?.(" ", "_") || targetInfo.spaceNumber;
        const style = targetInfo.highlight_color
          ? createHighlightStyle(targetInfo.highlight_color)
          : highlight_red;
        cadviewer.cvjs_highlightSpace(spaceNumber, style);
      }
    });
  } else if (searchResult.targetPlanInfo) {
    // Highlight single target (backward compatibility)
    const spaceNumber = searchResult.targetPlanInfo["spaceNumber"]?.replaceAll?.(".", "_").replaceAll?.(" ", "_") || searchResult.targetPlanInfo["spaceNumber"];
    if (spaceNumber) {
      const style = (searchResult.targetPlanInfo as any).highlight_color
        ? createHighlightStyle((searchResult.targetPlanInfo as any).highlight_color)
        : highlight_red;
      cadviewer.cvjs_highlightSpace(spaceNumber, style);
    }
  }

  // Highlight found spaces in green (or custom highlight_color if provided)
  if (searchResult.searchResults && searchResult.searchResults.length > 0) {
    for (let i = 0; i < searchResult.searchResults.length; i++) {
      if (searchResult.searchResults[i].spaceID) {
        const style = searchResult.searchResults[i].highlight_color
          ? createHighlightStyle(searchResult.searchResults[i].highlight_color!)
          : highlight_green;
        cadviewer.cvjs_highlightSpace(
          searchResult.searchResults[i].spaceID ?? "",
          style
        );
      }
    }
  }
}

/**
 * Highlight a single space using one of the relocation color conventions.
 * Used for click-to-focus interactions in RelocationCard.
 *
 * - "allowed": green (default for occupied allowed spaces)
 * - "vacant": yellow (highlights opportunity)
 * - "target": red (the store holding the relocation clause)
 */
export function highlightRelocationSpace(
  spaceNumber: string,
  variant: "allowed" | "vacant" | "target" = "allowed",
  clearExisting: boolean = true
): void {
  if (!spaceNumber) return;
  if (clearExisting) {
    cadviewer.cvjs_clearSpaceLayer();
  }
  const style =
    variant === "vacant"
      ? highlight_yellow
      : variant === "target"
        ? highlight_red
        : highlight_green;
  cadviewer.cvjs_highlightSpace(normalizeSpaceId(spaceNumber), style);
}

/**
 * Highlight all spaces declared in a hybrid_doc_sql.relocation response:
 *  - Allowed occupied spaces in green
 *  - Allowed vacant spaces in yellow (relocation opportunity)
 *  - The store holding the relocation clause (target) in red, if we can
 *    resolve a space_number from the details. Falls back to skipping the
 *    target highlight when no current space is exposed.
 */
export function highlightRelocationResults(
  relocation: RelocationData,
  clearExisting: boolean = true
): void {
  if (!relocation || !Array.isArray(relocation.results)) return;
  if (clearExisting) {
    cadviewer.cvjs_clearSpaceLayer();
  }

  const targetRaw = relocation.target_space
    ? String(relocation.target_space)
    : "";

  relocation.results.forEach((store) => {
    const detailsByNumber = new Map<string, RelocationSpaceDetail>();
    (store.allowed_spaces_details || []).forEach((d) => {
      if (d?.space_number) {
        detailsByNumber.set(String(d.space_number), d);
      }
    });

    const numbers =
      store.allowed_space_numbers && store.allowed_space_numbers.length > 0
        ? store.allowed_space_numbers
        : Array.from(detailsByNumber.keys());

    numbers.forEach((rawNumber) => {
      const numberStr = String(rawNumber);
      // The source space (target) is rendered last in red so it stands out
      // even if it also appears in the allowed list.
      if (targetRaw && numberStr === targetRaw) return;
      const spaceId = normalizeSpaceId(numberStr);
      const detail = detailsByNumber.get(numberStr);
      const style = detail?.highlight_color
        ? createHighlightStyle(detail.highlight_color)
        : isVacantTenant(detail?.tenant_name)
          ? highlight_yellow
          : highlight_green;
      cadviewer.cvjs_highlightSpace(spaceId, style);
    });
  });

  if (targetRaw) {
    cadviewer.cvjs_highlightSpace(normalizeSpaceId(targetRaw), highlight_red);
  }
}

/**
 * Process AI response for search results
 *
 * @param aijsonresponsestring The JSON response string from the AI
 * @param plansInfo The plans information data
 * @param distanceData The distance data
 * @returns Processed search results
 */
export function aiSearchCallback(
  aijsonresponsestring: any,
  plansInfo: any,
  distanceData: any
): NewSearchResult {
  const result: NewSearchResult = {
    targetPlanInfo: undefined,
    searchResults: [],
  };

  /*
  - `target_type`: Target establishment type (e.g., "women_apparel_store", "restaurant", etc.)
    - `max_distance`: Maximum distance, if a distance is not minimum or maxium use max_distance (numeric or null)
    - `min_distance`: Minimum distance (numeric or null)
    - `distance_unit`: Distance unit, must be chosen between "feet" -> for square_feet and "meters"
    - `is_within`: Boolean (true = within the specified distance, false = outside)
    - `availability`: Desired space status ("available", "any", etc.)
    - `min_area`: Minimum area (numeric or null)
    - `max_area`: Maximum area (numeric or null)
    - `target_name`: Target establishment name, shall always be present, null if not interpretated from the search string. If the brand is know 
*/
  // proximity search
  let action_type = "";
  let target_type = "";
  let max_distance = "";
  let min_distance = "";
  let distance_unit = "";
  let is_within_distance = false;
  let is_within_area = false;
  let min_area = 0;
  let max_area = 0;
  let area_unit = "";
  let target_name = "";

  let area_enabled = false;

  // seach information from JSON

  let GLA = "";
  let LeaseStartDate = "";
  let LeaseEndDate = "";
  let RentExpirationDate = "";
  let ActualFootprintSF = "";
  let TotalRent = "";
  let TotalRentPSF = "";

  // total sales 10.81.6
  let TotalSales = "";
  let TotalSalesPSF = "";

  // rent search
  let rent_enabled = false;

  let rent_criteria = "";
  let rent_type = "";
  let min_rent = "";
  let max_rent = "";
  let currency = "";

  // lease search

  let lease_enabled = false;

  let availability = "";
  let lease_criteria = "";
  let lease_status = "";
  let lease_expiration = "";

  // if are doing queries in meters we need to convert to meters
  let json_rent_unit_factor = 1;

  // other variables
  let thispace = "";

  const aijsonresponse = safeJSONParse(aijsonresponsestring);

  var myReturnArray: Array<{
    SpaceID: string;
    Distance: number;
    Area: number;
    Tenant: string;
    GLA: number;
    LeaseStartDate: string;
    LeaseEndDate: string;
    RentExpirationDate: string;
    ActualFootprintSF: string;
    TotalRent: string;
    TotalRentPSF: string;
    TotalSales: string;
    TotalSalesPSF: string;
  }> = [];

  const myReturnArray2: Array<{
    SpaceID: string;
    Distance: number;
  }> = [];

  var myReturnArray3: Array<{
    SpaceID: string;
    Distance: number;
    Area: number;
    Tenant: string;
    GLA: number;
    LeaseStartDate: string;
    LeaseEndDate: string;
    RentExpirationDate: string;
    ActualFootprintSF: string;
    TotalRent: string;
    TotalRentPSF: string;
    TotalSales: string;
    TotalSalesPSF: string;
  }> = [];

  try {
    /*
  {
    "search_criteria": {
      "proximity": {
        "enabled": true,
        "target_type": "fashion_store",
        "target_name": "Mango",
        "max_distance": 300,
        "min_distance": null,
        "distance_unit": "feet",
        "is_within_distance": true
      },
      "area": {
        "enabled": true,
        "min_area": 4000,
        "max_area": 6000,
        "area_unit": "square_feet"
      },
      "rent": {
        "enabled": true,
        "rent_criteria": "above",
        "rent_type": "per_square_foot",
        "min_rent": 400,
        "max_rent": null,
        "currency": "USD"
      },
      "lease": {
        "enabled": true,
        "availability": null,
        "lease_criteria": null,
        "lease_status": "ending_soon",
        "lease_expiration": "December 2026"
      }
    },
    "description": "Search for all storefronts less than 300 feet from Mango with an area between 4000 and 6000 sqf, lease expiring in December 2026, and rent above $400 per sqf."
  }

*/

    // Check if the response is valid
    //  if (aijsonresponse.search_criteria.action_type === "proximity_search") {
    if (true) {
      // Extract parameters from the AI JSON response

      target_name = aijsonresponse.search_criteria.proximity.target_name;
      target_type = aijsonresponse.search_criteria.proximity.target_type;
      max_distance = aijsonresponse.search_criteria.proximity.max_distance;
      min_distance = aijsonresponse.search_criteria.proximity.min_distance;
      distance_unit = aijsonresponse.search_criteria.proximity.distance_unit;
      is_within_distance =
        aijsonresponse.search_criteria.proximity.is_within_distance;
      is_within_area = aijsonresponse.search_criteria.proximity.is_within_area;

      try {
        // area search
        area_enabled = aijsonresponse.search_criteria.area.eabled;
        min_area = aijsonresponse.search_criteria.area.min_area;
        max_area = aijsonresponse.search_criteria.area.max_area;
        area_unit = aijsonresponse.search_criteria.area.area_unit;
      } catch (e) {
        console.log("Error parse serch AI response Area: ", e);
      }

      try {
        // rent search
        //    rent_enabled = aijsonresponse.search_criteria.rent.rent_enabled;
        rent_enabled = aijsonresponse.search_criteria.rent.enabled;
        rent_criteria = aijsonresponse.search_criteria.rent.rent_criteria;
        rent_type = aijsonresponse.search_criteria.rent.rent_type;
        min_rent = aijsonresponse.search_criteria.rent.min_rent;
        max_rent = aijsonresponse.search_criteria.rent.max_rent;
        currency = aijsonresponse.search_criteria.rent.currency;
      } catch (err) {
        console.log("Error parse serch AI response Rent: ", err);
      }

      try {
        // lease search
        lease_enabled = aijsonresponse.search_criteria.lease.enabled;
        availability = aijsonresponse.search_criteria.lease.availability;
        lease_criteria = aijsonresponse.search_criteria.lease.lease_criteria;
        lease_status = aijsonresponse.search_criteria.lease.lease_status;
        lease_expiration =
          aijsonresponse.search_criteria.lease.lease_expiration;
      } catch (err) {
        console.log("Error parse serch AI response Lease: ", err);
      }

      /*

    // mango test case
    action_type = "proximity_search";
    target_type = "retail_store";
    target_name = "Mango";
    max_distance = "null";
    min_distance =  "200";
    distance_unit =  "feet";
    is_within_distance =  false;
    availability = "available";
    min_area =  4000;
    max_area =  6000;
    area_unit =  "square_feet";
*/

      /*
      "action_type": "proximity_search",
     "parameters": {
     "target_type": "retail_store",
     "target_name": "Mango",
     "max_distance": null,
     "min_distance": 200,
     "distance_unit": "feet",
     "is_within_distance": false,
     "availability": "available",
     "min_area": 4000,
     "max_area": 6000,
     "area_unit": "square_feet"
  */

      /*
  // mango test case
    action_type = "proximity_search";
    target_type = "retail_store";
    target_name = "Mango";
    max_distance = "null";
    min_distance =  "200";
    distance_unit =  "feet";
    is_within_distance =  false;
    availability = "available";
    min_area =  4000;
    max_area =  6000;
    area_unit =  "square_feet";

*/

      /*
      "action_type": "proximity_search",
     "parameters": {
     "target_type": "retail_store",
     "target_name": "Mango",
     "max_distance": null,
     "min_distance": 200,
     "distance_unit": "feet",
     "is_within_distance": false,
     "availability": "available",
     "min_area": 4000,
     "max_area": 6000,
     "area_unit": "square_feet"
  */

      console.log("target_type: ", target_type);
      console.log("target_name: ", target_name);

      // check 1 - find the target name
      // the ai may not return a target name, so we need to set it to the target type
      if (
        target_name !== "" &&
        target_name !== undefined &&
        target_name !== null
      ) {
        // do nothing , all good
      } else if (
        (target_name === "" ||
          target_name === undefined ||
          target_name === null) &&
        target_type !== "" &&
        target_type !== undefined &&
        target_type !== null
      ) {
        target_name = target_type;
      } else {
        console.log("NOTE: target name and traget type are both empty");
        return result;
      }

      target_name = target_name.toLowerCase();

      // check 2 - find all data for the target name
      console.log(
        "target_name before checking against plansInfo: ",
        target_name
      );
      result.targetPlanInfo = (plansInfo?.spaces ?? []).find(
        (planInfo: any) => planInfo["spaceHeader"].toLowerCase() === target_name
      );
      console.log("planInfo: ", result.targetPlanInfo);

      if (result.targetPlanInfo == undefined || result.targetPlanInfo == null) {
        console.log("NOTE: target name not found in plansInfo");
        return result;
      }
      // we now have a target name
      // we now can check area and distance

      if (result.targetPlanInfo) {
        thispace = result.targetPlanInfo["spaceNumber"]
          .replaceAll(".", "_")
          .replaceAll(" ", "_");

        // check the area
        for (var key in plansInfo?.spaces ?? []) {
          let myarea = plansInfo?.spaces[key].detailTabs[0].detail.GLA;

          // I need all storefronts within 100m from Bulgari and with areas between 100 to 300 m2 and lease expiration before 2027 with current rent above $3000 pr m2

          // we need to check if the area is in square feet or meters
          console.log("CHECK myarea: ", myarea);

          if (myarea !== undefined || myarea !== null) {
            if (
              myarea.indexOf("m2") > -1 ||
              myarea.indexOf("m²") > -1 ||
              myarea.indexOf("m\u00B2") > -1
            ) {
              json_rent_unit_factor = 10.764;
              console.log(
                "CHECK myarea json_rent_unit_factor: ",
                json_rent_unit_factor
              );
            }
          }

          GLA = myarea;
          LeaseStartDate =
            plansInfo?.spaces[key].detailTabs[0].detail["Lease Start Date"];
          LeaseEndDate =
            plansInfo?.spaces[key].detailTabs[0].detail["Lease End Date"];
          RentExpirationDate =
            plansInfo?.spaces[key].detailTabs[0].detail["Rent Expiration Date"];

          // let us check if the area is in square feet or meters
          // 10.80.2
          ActualFootprintSF =
            plansInfo?.spaces[key].detailTabs[0].detail["Actual Footprint SF"];

          if (
            ActualFootprintSF !== undefined &&
            ActualFootprintSF !== null &&
            ActualFootprintSF !== ""
          ) {
          }
          else{
            ActualFootprintSF = plansInfo?.spaces[key].detailTabs[0].detail["Actual Footprint M2"];
            ActualFootprintSF = ActualFootprintSF.replace(" m²", "");
          }
          console.log("ActualFootprintSF: ", ActualFootprintSF);


          TotalRent = plansInfo?.spaces[key].detailTabs[0].detail["Total Rent"];


          TotalRentPSF =
            plansInfo?.spaces[key].detailTabs[0].detail["Total Rent PSF"];
          // 10.80.2 
          if (
            TotalRentPSF !== undefined &&
            TotalRentPSF !== null &&
            TotalRentPSF !== ""
          ) {
          }
          else{
            TotalRentPSF = plansInfo?.spaces[key].detailTabs[0].detail["Total Rent /M2"];
            TotalRentPSF = TotalRentPSF.replace(" m²", "");
          }
          console.log("TotalRentPSF: ", TotalRentPSF);




          /*        
      "Lease Start Date" : "3/1/2024",
      "Lease End Date" : "2/29/2028",
      "Rent Expiration Date" : "2/29/2028",
      "Actual Footprint SF" : "62.9 m\u00B2",
      "Total Rent": "$8038.62",
      "Total Rent PSF" : "$127.80",
*/

          console.log("myarea: ", myarea);

          if (myarea !== undefined) {
            let myareastring = myarea!.toString();
            myareastring = myareastring.replace(" m2", "");
            // Remove any commas
            myareastring = myareastring.replace(/,/g, "");
            myarea = parseFloat(myareastring);
          } else myarea = 0;

          // there can be no area indication in the JSON - the entire aray should be returned
          if (
            (min_area == undefined || min_area == null || min_area == 0) &&
            (max_area == undefined || max_area == null || max_area == 0)
          ) {
            myReturnArray.push({
              SpaceID: plansInfo?.spaces[key].spaceNumber,
              Distance: 0,
              Area: myarea,
              Tenant: plansInfo?.spaces[key].spaceHeader,
              GLA: parseFloat(GLA),
              LeaseStartDate: LeaseStartDate,
              LeaseEndDate: LeaseEndDate,
              RentExpirationDate: RentExpirationDate,
              ActualFootprintSF: ActualFootprintSF,
              TotalRent: TotalRent,
              TotalRentPSF: TotalRentPSF,
              TotalSales: TotalSales,
              TotalSalesPSF: TotalSalesPSF

            });
          }

          // there can be a min area indication in the JSON
          if (
            min_area !== undefined &&
            min_area !== null &&
            min_area !== 0 &&
            (max_area == undefined || max_area == null || max_area == 0)
          ) {
            if (myarea > min_area) {
              console.log("found area larger than min: ", myarea);
              myReturnArray.push({
                SpaceID: plansInfo?.spaces[key].spaceNumber,
                Distance: 0,
                Area: myarea,
                Tenant: plansInfo?.spaces[key].spaceHeader,
                GLA: parseFloat(GLA),
                LeaseStartDate: LeaseStartDate,
                LeaseEndDate: LeaseEndDate,
                RentExpirationDate: RentExpirationDate,
                ActualFootprintSF: ActualFootprintSF,
                TotalRent: TotalRent,
                TotalRentPSF: TotalRentPSF,
                TotalSales: TotalSales,
                TotalSalesPSF: TotalSalesPSF
              });
            }
          }

          // there can be a max area indication in the JSON
          if (
            min_area == undefined &&
            min_area == null &&
            min_area == 0 &&
            (max_area !== undefined || max_area !== null || max_area !== 0)
          ) {
            if (myarea < max_area) {
              console.log("found area less than max: ", myarea);
              myReturnArray.push({
                SpaceID: plansInfo?.spaces[key].spaceNumber,
                Distance: 0,
                Area: myarea,
                Tenant: plansInfo?.spaces[key].spaceHeader,
                GLA: parseFloat(GLA),
                LeaseStartDate: LeaseStartDate,
                LeaseEndDate: LeaseEndDate,
                RentExpirationDate: RentExpirationDate,
                ActualFootprintSF: ActualFootprintSF,
                TotalRent: TotalRent,
                TotalRentPSF: TotalRentPSF,
                TotalSales: TotalSales,
                TotalSalesPSF: TotalSalesPSF
              });
            }
          }

          // there can be a and min max area indication in the JSON
          if (
            min_area !== undefined &&
            min_area !== null &&
            min_area !== 0 &&
            (max_area !== undefined || max_area !== null || max_area !== 0)
          ) {
            if (myarea < max_area && myarea > min_area) {
              console.log("found area between min and max: ", myarea);
              myReturnArray.push({
                SpaceID: plansInfo?.spaces[key].spaceNumber,
                Distance: 0,
                Area: myarea,
                Tenant: plansInfo?.spaces[key].spaceHeader,
                GLA: parseFloat(GLA),
                LeaseStartDate: LeaseStartDate,
                LeaseEndDate: LeaseEndDate,
                RentExpirationDate: RentExpirationDate,
                ActualFootprintSF: ActualFootprintSF,
                TotalRent: TotalRent,
                TotalRentPSF: TotalRentPSF,
                TotalSales: TotalSales,
                TotalSalesPSF: TotalSalesPSF
              });
            }
          }
        }

        // we now have an array of spaces that are within the area
        /* debug highlight
    console.log("myReturnArray: ", myReturnArray, " we these spaces purple");
    for (var key in myReturnArray) {
      cadviewer.cvjs_highlightSpace(
        myReturnArray[key].SpaceID ?? "",
        highlight_purple
      );
    }
    */

        console.log("thisspace: ", thispace);

        // Check the distances
        const distanceInfo = (distanceData?.spaces ?? []).find(
          (distInfo: any) => distInfo["spaceNumber"] === thispace
        );

        console.log(
          "do we have distanceInfo for target space: ",
          distanceInfo,
          " min_distance: ",
          min_distance,
          " max_distance: ",
          max_distance
        );

        //*

        if (distanceInfo) {
          let thisdistance;
          const spaces = distanceInfo["distances"];

          for (var key2 in spaces ?? []) {
            thisdistance = spaces[key2].Distance;
            console.log(
              "thisdistance: ",
              thisdistance,
              " min_distance: ",
              min_distance,
              " max_distance: ",
              max_distance
            );
            console.log(
              min_distance == undefined ||
                min_distance == "null" ||
                min_distance == null ||
                min_distance == "0",
              max_distance == undefined ||
                max_distance == "null" ||
                max_distance == null ||
                max_distance == "0"
            );

            // there can be no area indication in the JSON - the entire aray should be returned
            if (
              (min_distance == undefined ||
                min_distance == "null" ||
                min_distance == null ||
                min_distance == "0") &&
              (max_distance == undefined ||
                max_distance == "null" ||
                max_distance == null ||
                max_distance == "0")
            ) {
              console.log("case1");

              myReturnArray2.push({
                SpaceID: spaces[key2].spaceNumber,
                Distance: thisdistance,
              });
            }

            console.log(
              min_distance !== undefined &&
                min_distance !== "null" &&
                min_distance !== null &&
                min_distance !== "0",
              max_distance == undefined ||
                max_distance == "null" ||
                max_distance == null ||
                max_distance == "0"
            );

            // there can be a min area indication in the JSON
            if (
              min_distance !== undefined &&
              min_distance !== "null" &&
              min_distance !== null &&
              min_distance !== "0" &&
              (max_distance == undefined ||
                max_distance == "null" ||
                max_distance == null ||
                max_distance == "0")
            ) {
              console.log("case2");
              let min_distance_number = parseFloat(min_distance);
              if (thisdistance > min_distance_number) {
                myReturnArray2.push({
                  SpaceID: spaces[key2].spaceNumber,
                  Distance: thisdistance,
                });
              }
            }

            console.log(
              min_distance == undefined ||
                min_distance == "null" ||
                min_distance == null ||
                min_distance == "0",
              max_distance !== undefined ||
                max_distance !== "null" ||
                max_distance !== null ||
                max_distance !== "0"
            );
            // there can be a max area indication in the JSON
            if (
              (min_distance == undefined ||
                min_distance == "null" ||
                min_distance == null ||
                min_distance == "0") &&
              (max_distance !== undefined ||
                max_distance !== "null" ||
                max_distance !== null ||
                max_distance !== "0")
            ) {
              console.log("case3");

              let max_distance_number = parseFloat(max_distance);
              if (thisdistance < max_distance_number) {
                myReturnArray2.push({
                  SpaceID: spaces[key2].spaceNumber,
                  Distance: thisdistance,
                });
              }
            }

            console.log(
              min_distance !== undefined &&
                min_distance !== "null" &&
                min_distance !== null &&
                min_distance !== "0",
              max_distance !== undefined &&
                max_distance !== "null" &&
                max_distance !== null &&
                max_distance !== "0"
            );

            // there can be a and min max area indication in the JSON
            if (
              min_distance !== undefined &&
              min_distance !== "null" &&
              min_distance !== null &&
              min_distance !== "0" &&
              max_distance !== undefined &&
              max_distance !== "null" &&
              max_distance !== null &&
              max_distance !== "0"
            ) {
              let min_distance_number = parseFloat(min_distance);
              let max_distance_number = parseFloat(max_distance);

              console.log("case4");

              if (
                thisdistance < max_distance_number &&
                thisdistance > min_distance_number
              ) {
                myReturnArray2.push({
                  SpaceID: spaces[key2].spaceNumber,
                  Distance: thisdistance,
                });
              }
            }
          }
        }

        // we now have an array of spaces that are within the distances

        /*  display for debug

    console.log("myReturnArray2: ", myReturnArray2, " we these spaces yellow");
    for (var key in myReturnArray2) {
      cadviewer.cvjs_highlightSpace(
        myReturnArray2[key].SpaceID ?? "",
        highlight_yellow
      );
    }

    */

        // now we combine the two arrays

        // Find matching spaces that meet both area and distance criteria
        for (var returnArrayKey in myReturnArray) {
          for (var returnArrayKey2 in myReturnArray2) {
            if (
              myReturnArray[returnArrayKey].SpaceID ===
              myReturnArray2[returnArrayKey2].SpaceID
            ) {
              console.log(
                "found match: ",
                myReturnArray[returnArrayKey].SpaceID,
                " ",
                myReturnArray2[returnArrayKey2].SpaceID
              );

              /*
          cadviewer.cvjs_highlightSpace(
            myReturnArray2[returnArrayKey2].SpaceID ?? "",
            highlight_green
          );
          */

              myReturnArray3.push({
                SpaceID: myReturnArray[returnArrayKey].SpaceID,
                Area: myReturnArray[returnArrayKey].Area,
                Tenant: myReturnArray[returnArrayKey].Tenant,
                Distance: myReturnArray2[returnArrayKey2].Distance,
                GLA: myReturnArray[returnArrayKey].GLA,
                LeaseStartDate: myReturnArray[returnArrayKey].LeaseStartDate,
                LeaseEndDate: myReturnArray[returnArrayKey].LeaseEndDate,
                RentExpirationDate:
                  myReturnArray[returnArrayKey].RentExpirationDate,
                ActualFootprintSF:
                  myReturnArray[returnArrayKey].ActualFootprintSF,
                TotalRent: myReturnArray[returnArrayKey].TotalRent,
                TotalRentPSF: myReturnArray[returnArrayKey].TotalRentPSF,
                TotalSales: myReturnArray[returnArrayKey].TotalSales,
                TotalSalesPSF: myReturnArray[returnArrayKey].TotalSalesPSF,
              });
            }
          }
        }
      }

      for (var returnArrayKey in myReturnArray3) {
        console.log("json_rent_unit_factor: ", json_rent_unit_factor);
        console.log(
          returnArrayKey + "  " + myReturnArray3[returnArrayKey].TotalRentPSF
        );
        console.log(
          returnArrayKey + "  " + myReturnArray3[returnArrayKey].LeaseEndDate
        );
      }

      // base casea rent search
      if (aijsonresponse.search_criteria.proximity.enabled === true) {
        console.log("proximity_search  - it is already done");
      }

      // base case lease search
      if (aijsonresponse.search_criteria.area.enabled === true) {
        console.log("area_search  - it is already done");
      }

      // base casea rent search
      if (aijsonresponse.search_criteria.rent.enabled === true) {
        /*
    // rent search
    rent_enabled = aijsonresponse.search_criteria.rent.rent_enabled;
    rent_criteria=aijsonresponse.search_criteria.rent.rent_criteria;
    rent_type = aijsonresponse.search_criteria.rent.rent_type;
    min_rent = aijsonresponse.search_criteria.rent.min_rent;
    max_rent = aijsonresponse.search_criteria.rent.max_rent;
    currency = aijsonresponse.search_criteria.rent.currency;
*/
        console.log(" rent search  - now done");

        myReturnArray = myReturnArray3;
        myReturnArray3 = [];
        let min_rent_number = parseFloat(min_rent);
        let max_rent_number = parseFloat(max_rent);
        for (var returnArrayKey in myReturnArray) {
          console.log(
            returnArrayKey + "  " + myReturnArray[returnArrayKey].TotalRentPSF
          );

          let rent_string = myReturnArray[returnArrayKey].TotalRentPSF;

          if (
            rent_string !== undefined &&
            rent_string !== null &&
            rent_string !== "" &&
            rent_string !== "undefined"
          ) {
            console.log("rent_string parsing: ", rent_string);

            // 10.80.4
            rent_string = rent_string.replace("$", "");
            rent_string = rent_string.replace("€", "");


            let rent_string_number =
              parseFloat(rent_string) * json_rent_unit_factor;

            console.log("rent_string_number: ", rent_string_number);

            if (
              min_rent_number !== undefined &&
              min_rent_number !== null &&
              min_rent_number !== 0
            ) {
              if (rent_string_number > min_rent_number) {
                myReturnArray3.push(myReturnArray[returnArrayKey]);
              }
            }

            if (
              max_rent_number !== undefined &&
              max_rent_number !== null &&
              max_rent_number !== 0
            ) {
              if (rent_string_number < max_rent_number) {
                myReturnArray3.push(myReturnArray[returnArrayKey]);
              }
            }
          }
        }

        for (var returnArrayKey in myReturnArray3) {
          console.log(
            "these are the rents:" +
              returnArrayKey +
              "  " +
              myReturnArray3[returnArrayKey].TotalRentPSF
          );
        }
      }

      // base casea lease search
      if (aijsonresponse.search_criteria.lease.enabled === true) {
        /*
    // lease search
    lease_enabled = aijsonresponse.search_criteria.lease.lease_enabled;
    availability = aijsonresponse.search_criteria.lease.availability;
    lease_criteria = aijsonresponse.search_criteria.lease.lease_criteria;
    lease_status = aijsonresponse.search_criteria.lease.lease_status;
    lease_expiration = aijsonresponse.search_criteria.lease.lease_expiration;
*/

        console.log(" lease search  - in process ");

        myReturnArray = myReturnArray3;
        myReturnArray3 = [];

        console.log("lease search: ", lease_status, " ", lease_expiration);

        for (var returnArrayKey in myReturnArray) {
          console.log(
            returnArrayKey + "  " + myReturnArray[returnArrayKey].LeaseEndDate
          );

          let lease_string = myReturnArray[returnArrayKey].LeaseEndDate;

          if (
            lease_string !== undefined &&
            lease_string !== null &&
            lease_string !== "" &&
            lease_string !== "undefined"
          ) {
            console.log("lease_string parsing: ", lease_string);

            // we have to parse the date string
            lease_string = lease_string.substring(
              lease_string.lastIndexOf("/") + 1
            );

            console.log("lease_string parsing: ", lease_string);

            if (
              lease_expiration !== undefined &&
              lease_expiration !== null &&
              lease_expiration !== ""
            ) {
              lease_expiration = lease_expiration.substring(
                lease_expiration.lastIndexOf(" ") + 1
              );

              if (lease_status == "ending_soon") {
                if (parseFloat(lease_string) <= parseFloat(lease_expiration)) {
                  myReturnArray3.push(myReturnArray[returnArrayKey]);
                }
              } else {
                if (parseFloat(lease_string) > parseFloat(lease_expiration)) {
                  myReturnArray3.push(myReturnArray[returnArrayKey]);
                }
              }
            }
          }

          for (var returnArrayKey in myReturnArray3) {
            console.log(
              "these are the leases:" +
                returnArrayKey +
                "  " +
                myReturnArray3[returnArrayKey].LeaseEndDate
            );
          }
        }
      }

      // now we have an array of spaces that are within the area and distance
      // we can simply add whatever we need for other types of searches

      console.log("this is the final return array: ", myReturnArray3);

      for (var returnArrayKey in myReturnArray3) {
        result.searchResults.push({
          spaceID: myReturnArray3[returnArrayKey].SpaceID,
          area: myReturnArray3[returnArrayKey].Area,
          tenant: myReturnArray3[returnArrayKey].Tenant,
          distance: myReturnArray3[returnArrayKey].Distance,
          totalRentPSF: "",
          leaseEndDate: "",
          totalRent: "",
          totalSales: myReturnArray3[returnArrayKey].TotalSales,
          totalSalesPSF: myReturnArray3[returnArrayKey].TotalSalesPSF,
        });
      }
      return result;

      // highlight the found spaces
    }
  } catch (e) {
    console.log("Error parsing AI JSON response: ", e);
  }

  return result;
}

// targetPlanInfo":{"spaceNumber":"2600","spaceHeader":"Bulgari","summary":{}}

/**
 * Process AI response for search results
 *
 * @param aijsonresponsestring The JSON response string from the AI
 * @param plansInfo The plans information data
 * @param distanceData The distance data
 * @returns Processed search results
 */
export function aiSearchCallbackV2(
  aijsonresponsestring: any,
  plansInfo: any,
  distanceData: any
): NewSearchResult {
  const result: NewSearchResult = {
    targetPlanInfo: undefined,
    searchResults: [],
  };

  console.log(
    "aiSearchCallbackV2 called with aijsonresponsestring: ",
    aijsonresponsestring
  );

  //{"targetPlanInfo":
  result.targetPlanInfo = JSON.parse(
    '{"spaceNumber":"","spaceHeader":"No Proximity Target","summary":{"GLA":"155.6 m²","Actual Footprint M2":"155.6 m²","Actual Footprint SF":"155.6 m²","Lease Start Date":"01/01/2016","Lease End Date":"12/31/2028"},"detailTabs":[{"caption":"Tenant Information (Bulgari)","detail":{"Mall Name":"Sample Property","Property #":"Sample Floor","DBA":"Bulgari","Space #":"2600","GLA":"155.6 m²","Lease Start Date":"6/1/2023","Lease End Date":"5/31/2028","Rent Expiration Date":"5/31/2028","Actual Footprint M2":"155.6 m²","Actual Footprint SF":"155.6 m²","Total Rent":"$58536.72","Total Rent /M2":"$376.20", "Total Rent PSF":"$376.20","Sales R12":"$223908.40","Sales R12 PSF":"$1439", "Sales R12 /M2":"$1439","Occupancy Cost":"29.05%","Occupancy Cost PSF":"$418", "Occupancy Cost /M2":"$418","Deal Type":"REN","Lease ID":"1985386-7","Lease Type":"Retail","Lease Status":"Open","Deal ID":"S6695311","Deal Stage":"Executed","Deal Record Type":"Permanent","Date of Possession (Mutually Agreed)":"6/1/2023","Date of Possession (Projected Per TC)":"6/1/2023","Store Opening (Projected Per TC)":"6/1/2023","Executed Date":"2/28/2021","Merchandise Category":"340 - All Other GAFO","Kickout Y/N (within 24 months)":"Yes"}}]}'
  );

  /*
  // if there is no proximity search we set a default targetPlanInfo
  result.targetPlanInfo = {
    spaceNumber: "",  
    spaceHeader: "No Target",
    summary: {},
    detailTabs: [          
      {
          "detail" : {
            "Mall Name" : "Sample Property",
            "Property #" : "Sample Floor",
            "DBA": "",
            "Space #": "",
            "GLA": 0,
            "Lease Start Date" : "3/1/2021",
            "Lease End Date" : "2/28/2029",
            "Rent Expiration Date" : "2/28/2029",
            "Actual Footprint SF" : 0,
            "Actual Footprint M2" : 0,
            "Total Rent": "$38748.6",
            "Total Rent PSF" : "$198",
            "Total Rent /M2" : "$198",
            "Sales R12": 0,
            "Sales R12 PSF" : 0,
            "Sales R12 /M2" : 0,
            "Occupancy Cost": 0,
            "Occupancy Cost PSF" : 0,
            "Occupancy Cost /M2" : 0,
            "Deal Type": "REN",
            "Lease ID" : "1941259-2",
            "Lease Type" : "Retail",
            "Lease Status" : "Open",
            "Deal ID" : "S3808622",
            "Deal Stage" : "Executed",
            "Deal Record Type" : "Permanent",
            "Date of Possession (Mutually Agreed)" : "3/1/2021",
            "Date of Possession (Projected Per TC)" : "3/1/2021",
            "Store Opening (Projected Per TC)" : "3/1/2021",
            "Executed Date": "9/30/2019",
            "Merchandise Category" : "340 - All Other GAFO",
            "Kickout Y/N (within 24 months)": "No",
            "Base Rent (annual)": 0,
            "Base Rent PSF": 0,
            "Gross Rent": 0,
            "Gross Rent PSF": 0,
            }
      }
    ]     
  };
*/

  // if are doing queries in meters we need to convert to meters
  let json_rent_unit_factor = 1;

  let feet_to_meters = 0.3048;
  let meters_to_feet = 3.28084;
  let square_feet_to_square_meters = 0.092903;
  let square_meters_to_square_feet = 10.7639;

  // other variables
  let thispace = "";

  //  const aijsonresponse = JSON.parse(aijsonresponsestring);

  const aijsonresponse = aijsonresponsestring;

  var myReturnArray: Array<{
    SpaceID: string;
    Distance: number;
    Area: number;
    Tenant: string;
    GLA: number;
    LeaseStartDate: string;
    LeaseEndDate: string;
    RentExpirationDate: string;
    ActualFootprintSF: string;
    TotalRent: string;
    TotalRentPSF: string;
    TotalSales: string;
    TotalSalesPSF: string;
  }> = [];

  let myReturnArray2: Array<{
    SpaceID: string;
    Distance: number;
  }> = [];

  let myReturnArray4: Array<{
    SpaceID: string;
    TotalRentPSF: string;
  }> = [];

  let myReturnArray5: Array<{
    SpaceID: string;
    LeaseEndDate: string;
  }> = [];

  let myReturnArray6: Array<{
    SpaceID: string;
    TotalRent: string;
  }> = [];


  let myReturnArray7: Array<{
    SpaceID: string;
    TotalSales: string;
  }> = [];

  let myReturnArray8: Array<{
    SpaceID: string;
    TotalSalesPSF: string;
  }> = [];



  let SearchResultTemp: Array<{
    spaceID: string;
    area: number;
    tenant: string;
    distance: number;
    totalRentPSF: string;
    leaseEndDate: string;
    totalRent: string;
    totalSales: string;
    totalSalesPSF: string;
  }> = [];

  let SearchResultTemp2: Array<{
    spaceID: string;
    area: number;
    tenant: string;
    distance: number;
    totalRentPSF: string;
    leaseEndDate: string;
    totalRent: string;
    totalSales: string;
    totalSalesPSF: string;
  }> = [];

  // proximity search
  let field = "";
  let operator = "";
  let referenceEntity = "";
  let referenceField = "";
  let distance = "";
  let distanceUnit = "";
  let value = "";
  let value2 = "";
  let action_type = "";


  // 10.81.6
  let areaUnit = ""

  let proximity_search = false;
  let area_search = false;
  let rent_search = false;
  let total_rent_search = false;
  let lease_search = false;
  let total_sales_search = false;
  let total_sales_search_per_unit = false;

  try {
    /*

{
    "success": true,
    "message": "I will search for stores within 50m of Bulgari, with an area between 1076 and 3229 SF (100-300 m²), rent above €3000/m² ($32,150/SF), and lease expiration in 2026.",
    "searchCriteria": [
        {
            "field": "proximity",
            "operator": "PROXIMITY",
            "referenceEntity": "Bulgari",
            "referenceField": "DBA",
            "distance": "50",
            "distanceUnit": "m"
        },
        {
            "field": "GLA",
            "operator": "BETWEEN",
            "value": "1076",
            "value2": "3229"
        },
        {
            "field": "Total Rent PSF",
            "operator": "UPPER_THAN",
            "value": "32150"
        },
        {
            "field": "Lease End Date",
            "operator": "EQUAL",
            "value": "12/31/2026"
        }
    ],
    "logic": "AND",
    "confidence": 0.9,
    "limitations": []
}
*/

    for (var i = 0; i < aijsonresponse.searchCriteria.length; i++) {
      console.log("searchCriteria: ", aijsonresponse.searchCriteria[i]);
      field = aijsonresponse.searchCriteria[i].field;
      operator = aijsonresponse.searchCriteria[i].operator;

      // PROXIMITY search
      if (aijsonresponse.searchCriteria[i].field === "proximity") {
        console.log("found proximity search");
        action_type = "proximity_search";
        proximity_search = true;

        // load in field values
        referenceEntity = aijsonresponse.searchCriteria[i].referenceEntity;
        referenceField = aijsonresponse.searchCriteria[i].referenceField;
        distance = aijsonresponse.searchCriteria[i].distance;
        distanceUnit = aijsonresponse.searchCriteria[i].distanceUnit;

        var target_name = referenceEntity.toLowerCase();
        // check 2 - find all data for the target name
        console.log(
          "target_name before checking against plansInfo: ",
          target_name
        );
        result.targetPlanInfo = (plansInfo?.spaces ?? []).find(
          (planInfo: any) =>
            planInfo["spaceHeader"].toLowerCase() === target_name
        );
        console.log("planInfo: ", result.targetPlanInfo);

        if (
          result.targetPlanInfo == undefined ||
          result.targetPlanInfo == null
        ) {
          console.log("NOTE: target name not found in plansInfo");
          return result;
        }
        myReturnArray2 = aiSearchCallbackV2_proximity(
          field,
          operator,
          referenceEntity,
          referenceField,
          distance,
          distanceUnit,
          result,
          distanceData
        );
        console.log(
          "this is the final proximity search array: ",
          myReturnArray2
        );

        // AREA SEARCH   // 10.80.2
      } else if (
        aijsonresponse.searchCriteria[i].field === "GLA" ||
        aijsonresponse.searchCriteria[i].field === "Area" ||
        aijsonresponse.searchCriteria[i].field === "Actual Footprint SF" ||
        aijsonresponse.searchCriteria[i].field === "Actual Footprint M2" ||
        aijsonresponse.searchCriteria[i].field === "summary.Actual Footprint SF" ||
        aijsonresponse.searchCriteria[i].field === "summary.Actual Footprint M2"
      ) {
        // area search
        console.log("found area search");
        action_type = "area_search";
        area_search = true;

        // load in field values
        value = aijsonresponse.searchCriteria[i].value;
        try {
          value2 = aijsonresponse.searchCriteria[i].value2;
        } catch (err) {
          console.log("error parsing value2 for lease search: ", err);
          value2 = "";
        }

        // 10.81.6
        let areaUnit = aijsonresponse.searchCriteria[i].areaUnit;
      

        myReturnArray = aiSearchCallbackV2_area(
          field,
          operator,
          value,
          value2,
          result,
          areaUnit,
          plansInfo
        );

        console.log("this is the final area search array: ", myReturnArray);
      } else if (
        aijsonresponse.searchCriteria[i].field === "Total Rent PSF" ||
        aijsonresponse.searchCriteria[i].field === "summary.Total Rent PSF" ||                  // the return is per square feet or square meter
        aijsonresponse.searchCriteria[i].field === "Total Rent /M2" ||
        aijsonresponse.searchCriteria[i].field === "summary.Total Rent /M2"                  // the return is per square feet or square meter
      ) {
        // rent search
        console.log("found rent search pr m2 or sqf");
        action_type = "rent_search_per_unit";
        total_rent_search = true;

        // load in field values
        value = aijsonresponse.searchCriteria[i].value;
        try {
          value2 = aijsonresponse.searchCriteria[i].value2;
        } catch (err) {
          console.log("error parsing value2 for lease search: ", err);
          value2 = "";
        }

        var unit;

        if (  aijsonresponse.searchCriteria[i].field === "Total Rent PSF" ||
              aijsonresponse.searchCriteria[i].field === "summary.Total Rent PSF" 
        ) {
          unit = "PSF";
        } else {
          unit = "M2";
        }

        // 10.81.6
        let areaUnit = aijsonresponse.searchCriteria[i].areaUnit;


        myReturnArray4 = aiSearchCallbackV2_totalrentarea(
          unit,
          areaUnit,
          field,
          operator,
          value,
          value2,
          result,
          plansInfo
        );

        console.log(
          "this is the final rent per square area search array: ",
          myReturnArray4
        );
      } else if (
        aijsonresponse.searchCriteria[i].field === "Total Rent" ||
        aijsonresponse.searchCriteria[i].field === "summary.Total Rent"
      ) {
        // rent search
        console.log("found total rent");
        action_type = "rent_search";
        rent_search = true;

        // load in field values
        value = aijsonresponse.searchCriteria[i].value;
        try {
          value2 = aijsonresponse.searchCriteria[i].value2;
        } catch (err) {
          console.log("error parsing value2 for lease search: ", err);
          value2 = "";
        }




        /*
            "field": "Total Rent",
            "operator": "UPPER_THAN",
            "value": "32150"
      */
        myReturnArray6 = aiSearchCallbackV2_totalrent(
          field,
          operator,
          value,
          value2,
          result,
          plansInfo
        );

        console.log(
          "this is the final total rent search array: ",
          myReturnArray6
        );
      } else if (
        aijsonresponse.searchCriteria[i].field === "summary.Lease End Date" ||
        aijsonresponse.searchCriteria[i].field === "Lease End Date" ||
        aijsonresponse.searchCriteria[i].field === "Lease Expiration"
      ) {
        // lease search
        console.log("found lease search");
        action_type = "lease_search";
        lease_search = true;

        // load in field values
        value = aijsonresponse.searchCriteria[i].value;
        try {
          value2 = aijsonresponse.searchCriteria[i].value2;
        } catch (err) {
          console.log("error parsing value2 for lease search: ", err);
          value2 = "";
        }
        myReturnArray5 = aiSearchCallbackV2_leaseenddate(
          field,
          operator,
          value,
          value2,
          result,
          plansInfo
        );

        console.log("this is the final lease search array: ", myReturnArray5);
      } // 10.80.7
      else if (
        aijsonresponse.searchCriteria[i].field === "summary.Sales R12" ||
        aijsonresponse.searchCriteria[i].field === "Sales R12" 
      ) {
        console.log("found sales search");

        action_type = "sales_search";
        total_sales_search = true;

        // load in field values
        value = aijsonresponse.searchCriteria[i].value;
        try {
          value2 = aijsonresponse.searchCriteria[i].value2;
        } catch (err) {
          console.log("error parsing value2 for sales search: ", err);
          value2 = "";
        }

        var unit;

        if (  aijsonresponse.searchCriteria[i].field === "Sales R12" ||
              aijsonresponse.searchCriteria[i].field === "summary.Sales R12" 
        ) {
          unit = "none";
        } else {
          unit = "none";
        }

        myReturnArray7 = aiSearchCallbackV2_totalsales(
          field,
          operator,
          value,
          value2,
          result,
          plansInfo
        );

        console.log(
          "this is the final rent search array: ",
          myReturnArray7
        );

      }
      else if (
        aijsonresponse.searchCriteria[i].field === "summary.Sales R12 PSF" ||
        aijsonresponse.searchCriteria[i].field === "Sales R12 /M2" 
      ) {
        console.log("found sales search price per m2 or sqf");


        action_type = "sales_search_pr_unit";
        total_sales_search_per_unit  = true;

        // load in field values
        value = aijsonresponse.searchCriteria[i].value;
        try {
          value2 = aijsonresponse.searchCriteria[i].value2;
        } catch (err) {
          console.log("error parsing value2 for sales search per unit: ", err);
          value2 = "";
        }

        var unit;

        if (  aijsonresponse.searchCriteria[i].field === "Sales R12 PSF" ||
              aijsonresponse.searchCriteria[i].field === "summary.Sales R12 PSF" 
        ) {
          unit = "PSF";
        } else {
          unit = "M2";
        }

        // 10.81.6
        let areaUnit = aijsonresponse.searchCriteria[i].areaUnit;




        myReturnArray8 = aiSearchCallbackV2_totalsalesperarea(
          unit,
          areaUnit,
          field,
          operator,
          value,
          value2,
          result,
          plansInfo
        );

        console.log(
          "this is the final rent per square area search array: ",
          myReturnArray8
        );

      }


    }

    console.log(
      "this is the final search result proximity myReturnArray2: ",
      myReturnArray2.length
    );
    console.log(
      "this is the final search result area myReturnArray: ",
      myReturnArray.length
    );
    console.log(
      "this is the final search result rent per m2 myReturnArray4: ",
      myReturnArray4.length
    );
    console.log(
      "this is the final search result total rent myReturnArray6: ",
      myReturnArray6.length
    );
    console.log(
      "this is the final search result lease myReturnArray5: ",
      myReturnArray5.length
    );

    console.log(
      "this is the final search total sales myReturnArray7: ",
      myReturnArray7.length
    );

    console.log(
      "this is the final search total sales per m2/sqf myReturnArray8: ",
      myReturnArray8.length
    );






    console.log("BEFORE geometry and area: ");
    SearchResultTemp = [];

    if (proximity_search == true && area_search == true) {
      for (var returnArrayKey in myReturnArray) {
        for (var returnArrayKey2 in myReturnArray2) {
          if (
            myReturnArray[returnArrayKey].SpaceID ===
            myReturnArray2[returnArrayKey2].SpaceID
          ) {
            console.log(
              "found match: ",
              myReturnArray[returnArrayKey].SpaceID,
              " ",
              myReturnArray2[returnArrayKey2].SpaceID
            );
            SearchResultTemp.push({
              spaceID: myReturnArray[returnArrayKey].SpaceID,
              area: myReturnArray[returnArrayKey].Area,
              tenant: myReturnArray[returnArrayKey].Tenant,
              distance: myReturnArray2[returnArrayKey2].Distance,
              totalRentPSF: myReturnArray[returnArrayKey].TotalRentPSF,
              leaseEndDate: myReturnArray[returnArrayKey].LeaseEndDate,
              totalRent: myReturnArray[returnArrayKey].TotalRent,
              totalSales: myReturnArray[returnArrayKey].TotalSales,
              totalSalesPSF: myReturnArray[returnArrayKey].TotalSalesPSF,
            });
          }
        }
      }
      console.log("geometry and area: ", SearchResultTemp.length);
      console.log("geometry and area: ", SearchResultTemp);
    } else if (proximity_search == true && area_search == false) {
      myReturnArray = aiSearchCallbackV2_allelements(result, plansInfo);

      for (var returnArrayKey in myReturnArray) {
        for (var returnArrayKey2 in myReturnArray2) {
          if (
            myReturnArray[returnArrayKey].SpaceID ===
            myReturnArray2[returnArrayKey2].SpaceID
          ) {
            console.log(
              "found match: ",
              myReturnArray[returnArrayKey].SpaceID,
              " ",
              myReturnArray2[returnArrayKey2].SpaceID
            );
            SearchResultTemp.push({
              spaceID: myReturnArray[returnArrayKey].SpaceID,
              area: myReturnArray[returnArrayKey].Area,
              tenant: myReturnArray[returnArrayKey].Tenant,
              distance: myReturnArray2[returnArrayKey2].Distance,
              totalRentPSF: myReturnArray[returnArrayKey].TotalRentPSF,
              leaseEndDate: myReturnArray[returnArrayKey].LeaseEndDate,
              totalRent: myReturnArray[returnArrayKey].TotalRent,
              totalSales: myReturnArray[returnArrayKey].TotalSales,
              totalSalesPSF: myReturnArray[returnArrayKey].TotalSalesPSF,
            });
          }
        }
      }

      //      SearchResultTemp = myReturnArray2;
      console.log("geometry and area (no area): ", SearchResultTemp.length);
      console.log("geometry and area: ", SearchResultTemp);
    } else if (proximity_search == false && area_search == true) {
      for (var returnArrayKey in myReturnArray) {
        console.log("found match: ", myReturnArray[returnArrayKey].SpaceID);
        SearchResultTemp.push({
          spaceID: myReturnArray[returnArrayKey].SpaceID,
          area: myReturnArray[returnArrayKey].Area,
          tenant: myReturnArray[returnArrayKey].Tenant,
          distance: -1,
          totalRentPSF: myReturnArray[returnArrayKey].TotalRentPSF,
          leaseEndDate: myReturnArray[returnArrayKey].LeaseEndDate,
          totalRent: myReturnArray[returnArrayKey].TotalRent,
          totalSales: myReturnArray[returnArrayKey].TotalSales,
          totalSalesPSF: myReturnArray[returnArrayKey].TotalSalesPSF,
        });
      }

      //      SearchResultTemp = myReturnArray;
      console.log(
        "geometry and area (no proximity): ",
        SearchResultTemp.length
      );
      console.log("geometry and area (no proximity): ", SearchResultTemp);
    } else {
      // no proximity or area search
      SearchResultTemp = [];

      //myReturnArray = aiSearchCallbackV2_allelements(result, plansInfo);

      console.log(
        "geometry and area (no area or proximity): ",
        SearchResultTemp.length
      );
      console.log(
        "geometry and area (no area or proximity): ",
        SearchResultTemp
      );
    }

    console.log("BEFORE apply total rent per m2: ");
    if (total_rent_search == true) {
      // add the full space information to search against
      if (proximity_search == false && area_search == false) {
        myReturnArray = aiSearchCallbackV2_allelements(result, plansInfo);

        for (var returnArrayKey in myReturnArray) {
          for (var returnArrayKey4 in myReturnArray4) {
            if (
              myReturnArray[returnArrayKey].SpaceID ===
              myReturnArray4[returnArrayKey4].SpaceID
            ) {
              console.log(
                "found match: ",
                myReturnArray[returnArrayKey].SpaceID,
                " ",
                myReturnArray4[returnArrayKey4].SpaceID
              );
              SearchResultTemp.push({
                spaceID: myReturnArray[returnArrayKey].SpaceID,
                area: myReturnArray[returnArrayKey].Area,
                tenant: myReturnArray[returnArrayKey].Tenant,
                distance: -1,
                totalRentPSF: myReturnArray[returnArrayKey].TotalRentPSF,
                leaseEndDate: myReturnArray[returnArrayKey].LeaseEndDate,
                totalRent: myReturnArray[returnArrayKey].TotalRent,
                totalSales: myReturnArray[returnArrayKey].TotalSales,
                totalSalesPSF: myReturnArray[returnArrayKey].TotalSalesPSF,
              });
            }
          }
        }
      } else {
        SearchResultTemp2 = SearchResultTemp;
        SearchResultTemp = [];

        for (var SearchResultTemp2Key in SearchResultTemp2) {
          for (var returnArrayKey4 in myReturnArray4) {
            if (
              SearchResultTemp2[SearchResultTemp2Key].spaceID ===
              myReturnArray4[returnArrayKey4].SpaceID
            ) {
              console.log(
                "found match: ",
                SearchResultTemp2[SearchResultTemp2Key].spaceID,
                " ",
                myReturnArray4[returnArrayKey4].SpaceID
              );
              SearchResultTemp.push({
                spaceID: SearchResultTemp2[SearchResultTemp2Key].spaceID,
                area: SearchResultTemp2[SearchResultTemp2Key].area,
                tenant: SearchResultTemp2[SearchResultTemp2Key].tenant,
                distance: SearchResultTemp2[SearchResultTemp2Key].distance,
                totalRentPSF:
                  SearchResultTemp2[SearchResultTemp2Key].totalRentPSF,
                leaseEndDate:
                  SearchResultTemp2[SearchResultTemp2Key].leaseEndDate,
                totalRent: SearchResultTemp2[SearchResultTemp2Key].totalRent,
                totalSales: SearchResultTemp2[SearchResultTemp2Key].totalSales,
                totalSalesPSF: SearchResultTemp2[SearchResultTemp2Key].totalSalesPSF,
              });
            }
          }
        }
      }
    }

    console.log("rent per m2: ", SearchResultTemp.length);
    console.log("rent per m2: ", SearchResultTemp);

    console.log("BEFORE apply total rent: ");

    if (rent_search == true) {
      // add the full space information to search against
      if (
        proximity_search == false &&
        area_search == false &&
        total_rent_search == false
      ) {
        myReturnArray = aiSearchCallbackV2_allelements(result, plansInfo);

        for (var returnArrayKey in myReturnArray) {
          for (var returnArrayKey6 in myReturnArray6) {
            if (
              myReturnArray[returnArrayKey].SpaceID ===
              myReturnArray6[returnArrayKey6].SpaceID
            ) {
              console.log(
                "found match: ",
                myReturnArray[returnArrayKey].SpaceID,
                " ",
                myReturnArray6[returnArrayKey6].SpaceID
              );
              SearchResultTemp.push({
                spaceID: myReturnArray[returnArrayKey].SpaceID,
                area: myReturnArray[returnArrayKey].Area,
                tenant: myReturnArray[returnArrayKey].Tenant,
                distance: -1,
                totalRentPSF: myReturnArray[returnArrayKey].TotalRentPSF,
                leaseEndDate: myReturnArray[returnArrayKey].LeaseEndDate,
                totalRent: myReturnArray[returnArrayKey].TotalRent,
                totalSales: myReturnArray[returnArrayKey].TotalSales,
                totalSalesPSF: myReturnArray[returnArrayKey].TotalSalesPSF,
              });
            }
          }
        }
      } else {
        SearchResultTemp2 = SearchResultTemp;
        SearchResultTemp = [];

        for (var SearchResultTemp2Key in SearchResultTemp2) {
          for (var returnArrayKey6 in myReturnArray6) {
            if (
              SearchResultTemp2[SearchResultTemp2Key].spaceID ===
              myReturnArray6[returnArrayKey6].SpaceID
            ) {
              console.log(
                "found match: ",
                SearchResultTemp2[SearchResultTemp2Key].spaceID,
                " ",
                myReturnArray6[returnArrayKey6].SpaceID
              );
              SearchResultTemp.push({
                spaceID: SearchResultTemp2[SearchResultTemp2Key].spaceID,
                area: SearchResultTemp2[SearchResultTemp2Key].area,
                tenant: SearchResultTemp2[SearchResultTemp2Key].tenant,
                distance: SearchResultTemp2[SearchResultTemp2Key].distance,
                totalRentPSF:
                  SearchResultTemp2[SearchResultTemp2Key].totalRentPSF,
                leaseEndDate:
                  SearchResultTemp2[SearchResultTemp2Key].leaseEndDate,
                totalRent: SearchResultTemp2[SearchResultTemp2Key].totalRent,
                totalSales: SearchResultTemp2[SearchResultTemp2Key].totalSales,
                totalSalesPSF: SearchResultTemp2[SearchResultTemp2Key].totalSalesPSF
              });
            }
          }
        }
      }
    }
    console.log("total rent: ", SearchResultTemp.length);
    console.log("total rent: ", SearchResultTemp);
    console.log("BEFORE apply lease: ");

    if (lease_search == true) {
      // add the full space information to search against
      if (
        proximity_search == false &&
        area_search == false &&
        total_rent_search == false &&
        rent_search == false
      ) {
        myReturnArray = aiSearchCallbackV2_allelements(result, plansInfo);
        for (var returnArrayKey in myReturnArray) {
          for (var returnArrayKey5 in myReturnArray5) {
            if (
              myReturnArray[returnArrayKey].SpaceID ===
              myReturnArray5[returnArrayKey5].SpaceID
            ) {
              console.log(
                "found match: ",
                myReturnArray[returnArrayKey].SpaceID,
                " ",
                myReturnArray5[returnArrayKey5].SpaceID
              );
              SearchResultTemp.push({
                spaceID: myReturnArray[returnArrayKey].SpaceID,
                area: myReturnArray[returnArrayKey].Area,
                tenant: myReturnArray[returnArrayKey].Tenant,
                distance: -1,
                totalRentPSF: myReturnArray[returnArrayKey].TotalRentPSF,
                leaseEndDate: myReturnArray[returnArrayKey].LeaseEndDate,
                totalRent: myReturnArray[returnArrayKey].TotalRent,
                totalSales: myReturnArray[returnArrayKey].TotalSales,
                totalSalesPSF: myReturnArray[returnArrayKey].TotalSalesPSF,
              });
            }
          }
        }
      } else {
        SearchResultTemp2 = SearchResultTemp;
        SearchResultTemp = [];
        console.log("SearchResultTemp: ", SearchResultTemp2, myReturnArray5);

        for (var SearchResultTemp2Key in SearchResultTemp2) {
          for (var returnArrayKey5 in myReturnArray5) {
            //console.log("check: ", SearchResultTemp2[SearchResultTemp2Key].spaceID, " ", myReturnArray5[returnArrayKey5].SpaceID);

            if (
              SearchResultTemp2[SearchResultTemp2Key].spaceID ===
              myReturnArray5[returnArrayKey5].SpaceID
            ) {
              console.log(
                "found match: ",
                SearchResultTemp2[SearchResultTemp2Key].spaceID,
                " ",
                myReturnArray5[returnArrayKey5].SpaceID
              );
              SearchResultTemp.push({
                spaceID: SearchResultTemp2[SearchResultTemp2Key].spaceID,
                area: SearchResultTemp2[SearchResultTemp2Key].area,
                tenant: SearchResultTemp2[SearchResultTemp2Key].tenant,
                distance: SearchResultTemp2[SearchResultTemp2Key].distance,
                totalRentPSF:
                  SearchResultTemp2[SearchResultTemp2Key].totalRentPSF,
                leaseEndDate:
                  SearchResultTemp2[SearchResultTemp2Key].leaseEndDate,
                totalRent: SearchResultTemp2[SearchResultTemp2Key].totalRent,
                totalSales: SearchResultTemp2[SearchResultTemp2Key].totalSales,
                totalSalesPSF: SearchResultTemp2[SearchResultTemp2Key].totalSalesPSF
              });
            }
          }
        }
      }
    }

    console.log("lease: ", SearchResultTemp.length);
    console.log("lease: ", SearchResultTemp);


    // if we have total sales search
    // 10.80.6
    if (total_sales_search == true) { 
      // add the full space information to search against
      if (
        proximity_search == false &&
        area_search == false &&
        total_rent_search == false &&
        rent_search == false &&
        lease_search == false
      ) {
        myReturnArray = aiSearchCallbackV2_allelements(result, plansInfo);
        for (var returnArrayKey in myReturnArray) {
          for (var returnArrayKey7 in myReturnArray7) {
            if (
              myReturnArray[returnArrayKey].SpaceID ===
              myReturnArray7[returnArrayKey7].SpaceID
            ) {
              console.log(
                "found match: ",
                myReturnArray[returnArrayKey].SpaceID,
                " ",
                myReturnArray7[returnArrayKey7].SpaceID
              );
              SearchResultTemp.push({
                spaceID: myReturnArray[returnArrayKey].SpaceID,
                area: myReturnArray[returnArrayKey].Area,
                tenant: myReturnArray[returnArrayKey].Tenant,
                distance: -1,
                totalRentPSF: myReturnArray[returnArrayKey].TotalRentPSF,
                leaseEndDate: myReturnArray[returnArrayKey].LeaseEndDate,
                totalRent: myReturnArray[returnArrayKey].TotalRent,
                totalSales: myReturnArray[returnArrayKey].TotalSales,
                totalSalesPSF: myReturnArray[returnArrayKey].TotalSalesPSF
              });
            }
          }
        }
      } else {
        SearchResultTemp2 = SearchResultTemp;
        SearchResultTemp = [];
        console.log("SearchResultTemp Array 7 sales R12: ", SearchResultTemp2, myReturnArray7);

        for (var SearchResultTemp2Key in SearchResultTemp2) {
          for (var returnArrayKey7 in myReturnArray7) {
            //console.log("check: ", SearchResultTemp2[SearchResultTemp2Key].spaceID, " ", myReturnArray7[returnArrayKey7].SpaceID);

            if (
              SearchResultTemp2[SearchResultTemp2Key].spaceID ===
              myReturnArray7[returnArrayKey7].SpaceID
            ) {
              console.log(
                "found match: ",
                SearchResultTemp2[SearchResultTemp2Key].spaceID,
                " ",
                myReturnArray7[returnArrayKey7].SpaceID
              );
              SearchResultTemp.push({
                spaceID: SearchResultTemp2[SearchResultTemp2Key].spaceID,
                area: SearchResultTemp2[SearchResultTemp2Key].area,
                tenant: SearchResultTemp2[SearchResultTemp2Key].tenant,
                distance: SearchResultTemp2[SearchResultTemp2Key].distance,
                totalRentPSF:
                  SearchResultTemp2[SearchResultTemp2Key].totalRentPSF,
                leaseEndDate:
                  SearchResultTemp2[SearchResultTemp2Key].leaseEndDate,
                totalRent: SearchResultTemp2[SearchResultTemp2Key].totalRent,
                totalSales: SearchResultTemp2[SearchResultTemp2Key].totalSales,
                totalSalesPSF: SearchResultTemp2[SearchResultTemp2Key].totalSalesPSF
              });
            }
          }
        }
      }


    }


    console.log("total sales: ", SearchResultTemp.length);
    console.log("total sales: ", SearchResultTemp);



    // if we have total sales search per unit
    // 10.80.7
    if (total_sales_search_per_unit == true) {

      if (
        proximity_search == false &&
        area_search == false &&
        total_rent_search == false &&
        rent_search == false &&
        lease_search == false &&
        total_sales_search == false
      ) {
        myReturnArray = aiSearchCallbackV2_allelements(result, plansInfo);
        for (var returnArrayKey in myReturnArray) {
          for (var returnArrayKey8 in myReturnArray8) {
            if (
              myReturnArray[returnArrayKey].SpaceID ===
              myReturnArray8[returnArrayKey8].SpaceID
            ) {
              console.log(
                "found match: ",
                myReturnArray[returnArrayKey].SpaceID,
                " ",
                myReturnArray8[returnArrayKey8].SpaceID
              );
              SearchResultTemp.push({
                spaceID: myReturnArray[returnArrayKey].SpaceID,
                area: myReturnArray[returnArrayKey].Area,
                tenant: myReturnArray[returnArrayKey].Tenant,
                distance: -1,
                totalRentPSF: myReturnArray[returnArrayKey].TotalRentPSF,
                leaseEndDate: myReturnArray[returnArrayKey].LeaseEndDate,
                totalRent: myReturnArray[returnArrayKey].TotalRent,
                totalSales: myReturnArray[returnArrayKey].TotalSales,
                totalSalesPSF: myReturnArray[returnArrayKey].TotalSalesPSF
              });
            }
          }
        }
      } else {
        SearchResultTemp2 = SearchResultTemp;
        SearchResultTemp = [];
        console.log("SearchResultTemp: Array8 sales per area ", SearchResultTemp2, myReturnArray8);

        for (var SearchResultTemp2Key in SearchResultTemp2) {
          for (var returnArrayKey8 in myReturnArray8) {
            //console.log("check: ", SearchResultTemp2[SearchResultTemp2Key].spaceID, " ", myReturnArray8[returnArrayKey8].SpaceID);

            if (
              SearchResultTemp2[SearchResultTemp2Key].spaceID ===
              myReturnArray8[returnArrayKey8].SpaceID
            ) {
              console.log(
                "found match: ",
                SearchResultTemp2[SearchResultTemp2Key].spaceID,
                " ",
                myReturnArray8[returnArrayKey8].SpaceID
              );
              SearchResultTemp.push({
                spaceID: SearchResultTemp2[SearchResultTemp2Key].spaceID,
                area: SearchResultTemp2[SearchResultTemp2Key].area,
                tenant: SearchResultTemp2[SearchResultTemp2Key].tenant,
                distance: SearchResultTemp2[SearchResultTemp2Key].distance,
                totalRentPSF:
                SearchResultTemp2[SearchResultTemp2Key].totalRentPSF,
                leaseEndDate:
                SearchResultTemp2[SearchResultTemp2Key].leaseEndDate,
                totalRent: SearchResultTemp2[SearchResultTemp2Key].totalRent,
                totalSales: SearchResultTemp2[SearchResultTemp2Key].totalSales,
                totalSalesPSF: SearchResultTemp2[SearchResultTemp2Key].totalSalesPSF
              });
            }
          }
        }
      }

    }

    console.log("total sales per unit: ", SearchResultTemp.length);
    console.log("total sales per unit: ", SearchResultTemp);


    // now we clean up the search results
    for (var SearchResultTempKey in SearchResultTemp) {
      if (lease_search === false)
        SearchResultTemp[SearchResultTempKey].leaseEndDate = "";
      if (total_rent_search === false)
        SearchResultTemp[SearchResultTempKey].totalRentPSF = "";
      if (rent_search === false)
        SearchResultTemp[SearchResultTempKey].totalRent = "";
      // 10.81.6
      if (total_sales_search === false)
        SearchResultTemp[SearchResultTempKey].totalSales = "";
      if (total_sales_search_per_unit === false)
        SearchResultTemp[SearchResultTempKey].totalSalesPSF = "";

    }

    result.searchResults = SearchResultTemp;
    SearchResultTemp = [];

    /*

    // let us consolidate
    //for (var returnArrayKey6 in myReturnArray6) {
      for (var returnArrayKey in myReturnArray) {
        for (var returnArrayKey2 in myReturnArray2) {
          for (var returnArrayKey4 in myReturnArray4) {
            for (var returnArrayKey5 in myReturnArray5) {
                if (myReturnArray[returnArrayKey].SpaceID === myReturnArray2[returnArrayKey2].SpaceID 
                  && myReturnArray4[returnArrayKey4].SpaceID === myReturnArray5[returnArrayKey5].SpaceID 
                  && myReturnArray2[returnArrayKey2].SpaceID === myReturnArray4[returnArrayKey4].SpaceID
                  / *&& myReturnArray6[returnArrayKey6].SpaceID === myReturnArray4[returnArrayKey4].SpaceID * /) {
                  // we have a match
                  console.log("found match: ", myReturnArray[returnArrayKey].SpaceID, " ", myReturnArray2[returnArrayKey2].SpaceID, " ", myReturnArray4[returnArrayKey4].SpaceID, " ", myReturnArray5[returnArrayKey5].SpaceID);
                  result.searchResults.push({
                  spaceID: myReturnArray[returnArrayKey].SpaceID,
                  area: myReturnArray[returnArrayKey].Area,
                  tenant: myReturnArray[returnArrayKey].Tenant,
                  distance: myReturnArray2[returnArrayKey2].Distance,
                });
              }  
            }
          }
        }
      }
    //}
*/
    console.log(
      "this is the final search result number of entities: ",
      result.searchResults.length
    );

    console.log("this is the final search result: ", result.searchResults);

    return result;

    // highlight the found spaces
  } catch (e) {
    console.log("Error parsing AI JSON response: ", e);
  }

  return result;
}

/**
 * Process AI response for proximity search results
 *
 * @param field The field to search
 * @param operator The operator to use for the search
 * @param referenceEntity The reference entity for the proximity search
 * @param referenceField The reference field for the proximity search
 * @param distance The distance for the proximity search
 * @param distanceUnit The unit of measurement for the distance
 */
export function aiSearchCallbackV2_proximity(
  field: any,
  operator: any,
  referenceEntity: any,
  referenceField: any,
  distance: any,
  distanceUnit: any,
  result: NewSearchResult,
  distanceData: any
): any {
  let myReturnArray2: Array<{
    SpaceID: string;
    Distance: number;
  }> = [];

  console.log(
    "aiSearchCallbackV2_proximity called with field: ",
    field,
    " operator: ",
    operator,
    " referenceEntity: ",
    referenceEntity,
    " referenceField: ",
    referenceField,
    " distance: ",
    distance,
    " distanceUnit: ",
    distanceUnit
  );
  // we now have a target name
  // we now can check area and distance
  if (result.targetPlanInfo) {
    let thispace = result.targetPlanInfo["spaceNumber"]
      .replaceAll(".", "_")
      .replaceAll(" ", "_");

    // Check the distances
    const distanceInfo = (distanceData?.spaces ?? []).find(
      (distInfo: any) => distInfo["spaceNumber"] === thispace
    );

    console.log(
      "do we have distanceInfo for target space: ",
      distanceInfo,
      " distance: ",
      distance,
      " distanceUnit: ",
      distanceUnit,
      "operator: ",
      operator
    );

    if (distanceInfo) {
      let thisdistance;
      const spaces = distanceInfo["distances"];

      for (var key2 in spaces ?? []) {
        thisdistance = spaces[key2].Distance;
        //console.log("thisdistance: ", thisdistance, " distance: ", distance, " distanceUnit: ", distanceUnit);

        // if no distance indication in the JSON - the entire aray should be returned
        if (
          operator === "PROXIMITY" &&
          (distance === undefined ||
            distance === "null" ||
            distance === null ||
            distance === "0")
        ) {
          //console.log("distance case1, no distance indication in the JSON");

          myReturnArray2.push({
            SpaceID: spaces[key2].spaceNumber,
            Distance: thisdistance,
          });
        }

        if (operator === "PROXIMITY" && thisdistance < distance) {
          //console.log("we have a PROXIMITY search, and the distance is less than the target distance");

          myReturnArray2.push({
            SpaceID: spaces[key2].spaceNumber,
            Distance: thisdistance,
          });
        }
      }
    }
  }

  return myReturnArray2;
}

/**
 * Process AI response for area search results
 *
 * @param field The field to search
 * @param operator The operator to use for the search
 * @param value The value for the area search
 * @param value2 The second value for the area search (if applicable)
 * @param result The result object to populate with search results
 * @param plansInfo The structure containing information about the spaces
 */
export function aiSearchCallbackV2_area(
  field: any,
  operator: any,
  value: any,
  value2: any,
  areaUnit: any,
  result: NewSearchResult,
  plansInfo: any
): any {
  var myReturnArray: Array<{
    SpaceID: string;
    Distance: number;
    Area: number;
    Tenant: string;
    GLA: number;
    LeaseStartDate: string;
    LeaseEndDate: string;
    RentExpirationDate: string;
    ActualFootprintSF: string;
    TotalRent: string;
    TotalRentPSF: string;
    TotalSales: string;
    TotalSalesPSF: string;
  }> = [];

  // we need to check if the area is in square feet or meters
  let value_m2 = 1; // convert to square meters
  let value2_m2 = 1; // convert to square meters

  // check the area
  for (var key in plansInfo?.spaces ?? []) {
    let myarea = plansInfo?.spaces[key].detailTabs[0].detail.GLA;

    if (myarea !== undefined) {
      let myareastring = myarea!.toString();
      myareastring = myareastring.replace(" m2", "");
      // Remove any commas
      myareastring = myareastring.replace(/,/g, "");
      myarea = parseFloat(myareastring);
    } else myarea = 0;

    // we need to check if the area is in square feet or meters
    value_m2 = parseFloat(value); /// 10.764; // convert to square meters
    value2_m2 = parseFloat(value2); // / 10.764; // convert to square meters

    //console.log("checking area for space: ", plansInfo?.spaces[key].spaceHeader, " myarea: ", myarea, " value_m2: ", value_m2, " value2_m2: ", value2_m2);

    if (value_m2 % 1 != 0 || value_m2 > 20000) {
      console.log(
        "value_m2 is not an integer or too large, converting to square meters"
      );
      value_m2 = value_m2 / 10.764;
    }

    /*
        if (operator === "LOwER_THAN" || operator === "UPPER_THAN") {
          value_m2 = value_m2/10.764; // convert to square meters
        }
        */

    let GLA = myarea;
    let LeaseStartDate =
      plansInfo?.spaces[key].detailTabs[0].detail["Lease Start Date"];
    let LeaseEndDate =
      plansInfo?.spaces[key].detailTabs[0].detail["Lease End Date"];
    let RentExpirationDate =
      plansInfo?.spaces[key].detailTabs[0].detail["Rent Expiration Date"];

    // 10.80.2
/*  previous
    let ActualFootprintSF =
      plansInfo?.spaces[key].detailTabs[0].detail["Actual Footprint SF"];
    
    let TotalRent = plansInfo?.spaces[key].detailTabs[0].detail["Total Rent"];
    
    let TotalRentPSF =
      plansInfo?.spaces[key].detailTabs[0].detail["Total Rent PSF"];
*/

        // let us check if the area is in square feet or meters
        // 10.80.2
        let ActualFootprintSF =
          plansInfo?.spaces[key].detailTabs[0].detail["Actual Footprint SF"];

        console.log("ActualFootprintSF: ", ActualFootprintSF);

        if (
          ActualFootprintSF !== undefined &&
          ActualFootprintSF !== "undefined" &&
          ActualFootprintSF !== null &&
          ActualFootprintSF !== ""
        ) {
          console.log("ActualFootprintSF is defined: ", ActualFootprintSF);
          // we have a value
        }
        else{
          console.log("ActualFootprintSF is not defined: ", ActualFootprintSF);
          ActualFootprintSF = plansInfo?.spaces[key].detailTabs[0].detail["Actual Footprint M2"];
          if (ActualFootprintSF!= undefined && ActualFootprintSF != null) {
            ActualFootprintSF = ActualFootprintSF.replace(" m²", "");
          }
        }
        console.log("ActualFootprintSF: ", ActualFootprintSF);


        let TotalRent = plansInfo?.spaces[key].detailTabs[0].detail["Total Rent"];
        let TotalRentPSF =
          plansInfo?.spaces[key].detailTabs[0].detail["Total Rent PSF"];


        // 10.80.2 

        if (
          TotalRentPSF !== undefined &&
          TotalRentPSF !== "undefined" &&
          TotalRentPSF !== null &&
          TotalRentPSF !== ""
        ) {
          console.log("TotalRentPSF is defined: ", TotalRentPSF);
          // we have a value
        }
        else{
          console.log("TotalRentPSF not defined: ", TotalRentPSF);
          TotalRentPSF = plansInfo?.spaces[key].detailTabs[0].detail["Total Rent /M2"];
          if (TotalRentPSF != undefined && TotalRentPSF != null){
            // we have a value
            TotalRentPSF = TotalRentPSF.replace(" m²", "");
      
          }
        }
        console.log("TotalRentPSF: ", TotalRentPSF);


        // 10.81.6
    // 10.80.6
    let TotalSales = plansInfo?.spaces[key].detailTabs[0].detail["Sales R12"];

    let TotalSalesPSF = plansInfo?.spaces[key].detailTabs[0].detail["Sales R12 PSF"];

    if (
      TotalSalesPSF !== undefined &&
      TotalSalesPSF !== null &&
      TotalSalesPSF !== ""
    ) {
    }
    else{
      TotalSalesPSF = plansInfo?.spaces[key].detailTabs[0].detail["Sales R12 /M2"];
    }




    // we solve this  - this is only if there are two values in the area search
    if (operator === "BETWEEN" && myarea < value2_m2 && myarea > value_m2) {
      //console.log("found area between min and max: ", myarea);
      myReturnArray.push({
        SpaceID: plansInfo?.spaces[key].spaceNumber,
        Distance: 0,
        Area: myarea,
        Tenant: plansInfo?.spaces[key].spaceHeader,
        GLA: parseFloat(GLA),
        LeaseStartDate: LeaseStartDate,
        LeaseEndDate: LeaseEndDate,
        RentExpirationDate: RentExpirationDate,
        ActualFootprintSF: ActualFootprintSF,
        TotalRent: TotalRent,
        TotalRentPSF: TotalRentPSF,
        TotalSales: TotalSales,
        TotalSalesPSF: TotalSalesPSF

      });
    } else if (operator === "LOWER_THAN" && myarea < value) {
      //console.log("found area lower than: ", myarea);
      myReturnArray.push({
        SpaceID: plansInfo?.spaces[key].spaceNumber,
        Distance: 0,
        Area: myarea,
        Tenant: plansInfo?.spaces[key].spaceHeader,
        GLA: parseFloat(GLA),
        LeaseStartDate: LeaseStartDate,
        LeaseEndDate: LeaseEndDate,
        RentExpirationDate: RentExpirationDate,
        ActualFootprintSF: ActualFootprintSF,
        TotalRent: TotalRent,
        TotalRentPSF: TotalRentPSF,
        TotalSales: TotalSales,
        TotalSalesPSF: TotalSalesPSF
      });
    } else if (operator === "UPPER_THAN" && myarea > value) {
      //console.log("found area upper than: ", myarea);
      myReturnArray.push({
        SpaceID: plansInfo?.spaces[key].spaceNumber,
        Distance: 0,
        Area: myarea,
        Tenant: plansInfo?.spaces[key].spaceHeader,
        GLA: parseFloat(GLA),
        LeaseStartDate: LeaseStartDate,
        LeaseEndDate: LeaseEndDate,
        RentExpirationDate: RentExpirationDate,
        ActualFootprintSF: ActualFootprintSF,
        TotalRent: TotalRent,
        TotalRentPSF: TotalRentPSF,
        TotalSales: TotalSales,
        TotalSalesPSF: TotalSalesPSF
      });
    }
  }

  return myReturnArray;
}

/**
 * Create a return array with all elements
 *
 * @param result The result object to populate with search results
 * @param plansInfo The structure containing information about the spaces
 */
export function aiSearchCallbackV2_allelements(
  result: NewSearchResult,
  plansInfo: any
): any {
  var myReturnArray: Array<{
    SpaceID: string;
    Distance: number;
    Area: number;
    Tenant: string;
    GLA: number;
    LeaseStartDate: string;
    LeaseEndDate: string;
    RentExpirationDate: string;
    ActualFootprintSF: string;
    TotalRent: string;
    TotalRentPSF: string;
    TotalSales: string;
    TotalSalesPSF: string;
  }> = [];

  // check the area
  for (var key in plansInfo?.spaces ?? []) {
    let GLA = plansInfo?.spaces[key].detailTabs[0].detail["GLA"];
    let LeaseStartDate =
      plansInfo?.spaces[key].detailTabs[0].detail["Lease Start Date"];
    let LeaseEndDate =
      plansInfo?.spaces[key].detailTabs[0].detail["Lease End Date"];
    let RentExpirationDate =
      plansInfo?.spaces[key].detailTabs[0].detail["Rent Expiration Date"];
    let ActualFootprintSF =
      plansInfo?.spaces[key].detailTabs[0].detail["Actual Footprint SF"];
    // 10.80.4
    if (
      ActualFootprintSF !== undefined &&
      ActualFootprintSF !== null &&
      ActualFootprintSF !== ""
    ) {
    }
    else{
      ActualFootprintSF = plansInfo?.spaces[key].detailTabs[0].detail["Actual Footprint M2"];
    }



    let TotalRent = plansInfo?.spaces[key].detailTabs[0].detail["Total Rent"];
    
    
    let TotalRentPSF =
      plansInfo?.spaces[key].detailTabs[0].detail["Total Rent PSF"];
    // 10.80.4

    // 10.80.4
    if (
      TotalRentPSF !== undefined &&
      TotalRentPSF !== null &&
      TotalRentPSF !== ""
    ) {
    }
    else{
      TotalRentPSF = plansInfo?.spaces[key].detailTabs[0].detail["Total Rent /M2"];
    }

    // 10.80.6
    let TotalSales = plansInfo?.spaces[key].detailTabs[0].detail["Sales R12"];

    let TotalSalesPSF = plansInfo?.spaces[key].detailTabs[0].detail["Sales R12 PSF"];

    if (
      TotalSalesPSF !== undefined &&
      TotalSalesPSF !== null &&
      TotalSalesPSF !== ""
    ) {
    }
    else{
      TotalSalesPSF = plansInfo?.spaces[key].detailTabs[0].detail["Sales R12 /M2"];
    }



    myReturnArray.push({
      SpaceID: plansInfo?.spaces[key].spaceNumber,
      Distance: 0,
      Area: parseFloat(GLA),
      Tenant: plansInfo?.spaces[key].spaceHeader,
      GLA: parseFloat(GLA),
      LeaseStartDate: LeaseStartDate,
      LeaseEndDate: LeaseEndDate,
      RentExpirationDate: RentExpirationDate,
      ActualFootprintSF: ActualFootprintSF,
      TotalRent: TotalRent,
      TotalRentPSF: TotalRentPSF,
      TotalSales: TotalSales,
      TotalSalesPSF: TotalSalesPSF
    });
  }
  return myReturnArray;
}

// 10.80.7

/**
 * Process AI response for total sales area search results
 *
 * @param unit Unit of measurement for the total sales area search
 * @param field The field to search
 * @param operator The operator to use for the search
 * @param value The value for the total sales area search
 * @param value2 The second value for the total sales area search (for BETWEEN operator)
 * @param result The result object to populate with search results
 * @param plansInfo The structure containing information about the spaces
 */
export function aiSearchCallbackV2_totalsalesperarea(
  unit: any,
  areaUnit: any,
  field: any,
  operator: any,
  value: any,
  value2: any,
  result: NewSearchResult,
  plansInfo: any
): any {
  let myReturnArray8: Array<{
    SpaceID: string;
    TotalSalesPSF: string;
  }> = [];

  /*
            "field": "Total Rent PSF",
            "operator": "UPPER_THAN",
            "value": "32150"
      */
  value = value.replace("$", "");
  value = value.replace("€", "");
  value = value.replace(/,/g, "");
  let value_ = parseFloat(value);
  // scale to per square foot
  //value_ = value_ / 10.764; // convert to square feet

  console.log("before processing JSON structure value_: ", value_);

  for (var key in plansInfo?.spaces ?? []) {
    let TotalSalesPSF =
      plansInfo?.spaces[key].detailTabs[0].detail["Sales R12 PSF"];

    // 10.80.3
    if (
      TotalSalesPSF !== undefined &&
      TotalSalesPSF !== "undefined" &&
      TotalSalesPSF !== null &&
      TotalSalesPSF !== ""
    ) {
      console.log("TotalSalesPSF is defined: ", TotalSalesPSF);

      TotalSalesPSF = TotalSalesPSF.replace("$", "");
      TotalSalesPSF = TotalSalesPSF.replace("€", "");
      TotalSalesPSF = TotalSalesPSF.replace(/,/g, "");
      TotalSalesPSF = parseFloat(TotalSalesPSF);

      // we have a value
    } else {
      console.log("Sales R12 not defined: ", TotalSalesPSF);
      TotalSalesPSF = plansInfo?.spaces[key].detailTabs[0].detail["Sales R12 /M2"];
      if (TotalSalesPSF != undefined && TotalSalesPSF != null){
        // we have a value
        TotalSalesPSF = TotalSalesPSF.replace("$", "");
        TotalSalesPSF = TotalSalesPSF.replace("€", "");
        TotalSalesPSF = TotalSalesPSF.replace(/,/g, "");

        // 10.80.3
        TotalSalesPSF = parseFloat(TotalSalesPSF);

        // we now need to adjust the value_ to be per square meter
        if (unit === "m2" || unit === "M2") {
          console.log("unit is m2 in JSON call, we do not have to convert");
          // no conversion value_ = value_ / 10.764; // convert to square meters
        }
        else{
          console.log("unit is sqf in JSON call, we have to convert");
          value_ = value_ / 10.764; // convert to square meters
        }

      }
    }
    /*
    if (
      TotalRentPSF === undefined ||
      TotalRentPSF === null ||
      TotalRentPSF === ""
    )
      continue;
*/

    console.log("after processing JSON structure value_: ", value_, " TotalSalesPSF: ", TotalSalesPSF);

    //TotalRentPSF = parseFloat(TotalRentPSF.replace("$", "").replace(/,/g, ""))

    //console.log("value_: ", value_, " TotalRentPSF: ", TotalRentPSF);

    // we solve this  - this is only if there are two values in the area search
    if (
      operator === "UPPER_THAN" &&
      TotalSalesPSF !== undefined &&
      TotalSalesPSF !== null &&
      value_ < TotalSalesPSF
    ) {
      //console.log("found area between min and max: ", myarea);
      myReturnArray8.push({
        SpaceID: plansInfo?.spaces[key].spaceNumber,
        TotalSalesPSF: TotalSalesPSF.toString(),
      });
    }

    // we solve this  - this is only if there are two values in the area search
    if (
      operator === "LOWER_THAN" &&
      TotalSalesPSF !== undefined &&
      TotalSalesPSF !== null &&
      value_ > TotalSalesPSF
    ) {
      //console.log("found area between min and max: ", myarea);
      myReturnArray8.push({
        SpaceID: plansInfo?.spaces[key].spaceNumber,
        TotalSalesPSF: TotalSalesPSF.toString(),
      });
    }

      // 10.80.3
    if (
      operator === "BETWEEN" &&
      TotalSalesPSF !== undefined &&
      TotalSalesPSF !== null
    ) {

    value2 = value2.replace("$", "");
    value2 = value2.replace("€", "");
    value2 = value2.replace(/,/g, "");
    let value2_ = parseFloat(value2);

      if (value_ < TotalSalesPSF && value2_ > TotalSalesPSF) {

      console.log("found area between min and max: ", TotalSalesPSF, value_, value2_);
      myReturnArray8.push({
        SpaceID: plansInfo?.spaces[key].spaceNumber,
        TotalSalesPSF: TotalSalesPSF.toString(),
      });


      }


    }
  }

  return myReturnArray8;
}















// 10.80.7




/**
 * Process AI response for total rent area search results
 *
 * @param unit Unit of measurement for the total rent area search
 * @param field The field to search
 * @param operator The operator to use for the search
 * @param value The value for the total rent area search
 * @param value2 The second value for the total rent area search (for BETWEEN operator)
 * @param result The result object to populate with search results
 * @param plansInfo The structure containing information about the spaces
 */
export function aiSearchCallbackV2_totalrentarea(
  unit: any,
  areaUnit: any,
  field: any,
  operator: any,
  value: any,
  value2: any,
  result: NewSearchResult,
  plansInfo: any
): any {
  let myReturnArray4: Array<{
    SpaceID: string;
    TotalRentPSF: string;
  }> = [];

  /*
            "field": "Total Rent PSF",
            "operator": "UPPER_THAN",
            "value": "32150"
      */
  value = value.replace("$", "");
  value = value.replace("€", "");
  value = value.replace(/,/g, "");
  let value_ = parseFloat(value);
  // scale to per square foot
  //value_ = value_ / 10.764; // convert to square feet

  console.log("before processing JSON structure value_: ", value_);

  for (var key in plansInfo?.spaces ?? []) {
    let TotalRentPSF =
      plansInfo?.spaces[key].detailTabs[0].detail["Total Rent PSF"];

    // 10.80.3
    if (
      TotalRentPSF !== undefined &&
      TotalRentPSF !== "undefined" &&
      TotalRentPSF !== null &&
      TotalRentPSF !== ""
    ) {
      console.log("TotalRentPSF is defined: ", TotalRentPSF);

      TotalRentPSF = TotalRentPSF.replace("$", "");
      TotalRentPSF = TotalRentPSF.replace("€", "");
      TotalRentPSF = TotalRentPSF.replace(/,/g, "");
      TotalRentPSF = parseFloat(TotalRentPSF);

      // we have a value
    } else {
      console.log("TotalRentPSF not defined: ", TotalRentPSF);
      TotalRentPSF = plansInfo?.spaces[key].detailTabs[0].detail["Total Rent /M2"];
      if (TotalRentPSF != undefined && TotalRentPSF != null){
        // we have a value
        TotalRentPSF = TotalRentPSF.replace("$", "");
        TotalRentPSF = TotalRentPSF.replace("€", "");
        TotalRentPSF = TotalRentPSF.replace(/,/g, "");

        // 10.80.3
        TotalRentPSF = parseFloat(TotalRentPSF);

        // we now need to adjust the value_ to be per square meter
        if (unit === "m2" || unit === "M2") {
          console.log("unit is m2 in JSON call, we do not have to convert");
          // no conversion value_ = value_ / 10.764; // convert to square meters
        }
        else{
          console.log("unit is sqf in JSON call, we have to convert");
          value_ = value_ / 10.764; // convert to square meters
        }

      }
    }
    /*
    if (
      TotalRentPSF === undefined ||
      TotalRentPSF === null ||
      TotalRentPSF === ""
    )
      continue;
*/
  
    console.log("after processing JSON structure value_: ", value_, " TotalRentPSF: ", TotalRentPSF);

    //TotalRentPSF = parseFloat(TotalRentPSF.replace("$", "").replace(/,/g, ""))

    //console.log("value_: ", value_, " TotalRentPSF: ", TotalRentPSF);

    // we solve this  - this is only if there are two values in the area search
    if (
      operator === "UPPER_THAN" &&
      TotalRentPSF !== undefined &&
      TotalRentPSF !== null &&
      value_ < TotalRentPSF
    ) {
      //console.log("found area between min and max: ", myarea);
      myReturnArray4.push({
        SpaceID: plansInfo?.spaces[key].spaceNumber,
        TotalRentPSF: TotalRentPSF.toString(),
      });
    }

    // we solve this  - this is only if there are two values in the area search
    if (
      operator === "LOWER_THAN" &&
      TotalRentPSF !== undefined &&
      TotalRentPSF !== null &&
      value_ > TotalRentPSF
    ) {
      //console.log("found area between min and max: ", myarea);
      myReturnArray4.push({
        SpaceID: plansInfo?.spaces[key].spaceNumber,
        TotalRentPSF: TotalRentPSF.toString(),
      });
    }

      // 10.80.3
    if (
      operator === "BETWEEN" &&
      TotalRentPSF !== undefined &&
      TotalRentPSF !== null
    ) {

    value2 = value2.replace("$", "");
    value2 = value2.replace("€", "");
    value2 = value2.replace(/,/g, "");
    let value2_ = parseFloat(value2);

      if (value_ < TotalRentPSF && value2_ > TotalRentPSF) {

      console.log("found area between min and max: ", TotalRentPSF, value_, value2_);
      myReturnArray4.push({
        SpaceID: plansInfo?.spaces[key].spaceNumber,
        TotalRentPSF: TotalRentPSF.toString(),
      });


      }


    }
  }

  return myReturnArray4;
}

// 10.80.7


/**
 * Process AI response for total sales search results
 *
 * @param field The field to search
 * @param operator The operator to use for the search
 * @param value The value for the total sales search
 * @param value2 The value for the total sales search
 * @param result The result object to populate with search results
 * @param plansInfo The structure containing information about the spaces
 */
export function aiSearchCallbackV2_totalsales(
  field: any,
  operator: any,
  value: any,
  value2: any,
  result: NewSearchResult,
  plansInfo: any
): any {
  let myReturnArray7: Array<{
    SpaceID: string;
    TotalSales: string;
  }> = [];

  value = value.replace("$", "");
  value = value.replace("€", "");
  value = value.replace(/,/g, "");
  let value_ = parseFloat(value);
  // scale to per square foot
  //value_ = value_ / 10.764; // convert to square feet

  console.log("value_: ", value_);

  for (var key in plansInfo?.spaces ?? []) {
    let TotalSales = plansInfo?.spaces[key].detailTabs[0].detail["Sales R12"];

    if (TotalSales === undefined || TotalSales === null || TotalSales === "")
      continue;

    TotalSales = TotalSales.replace("$", "");
    TotalSales = TotalSales.replace("€", "");
    TotalSales = TotalSales.replace(/,/g, "");
    TotalSales = parseFloat(TotalSales);

    console.log("value_: ", value_, " TotalSales: ", TotalSales, " operator: ", operator);

    //TotalRentPSF = parseFloat(TotalRentPSF.replace("$", "").replace(/,/g, ""))
    //console.log("value_: ", value_, " TotalRentPSF: ", TotalRentPSF);

    // we solve this  - this is only if there are two values in the area search

    if (
      operator === "EQUAL" &&
      TotalSales !== undefined &&
      TotalSales !== null &&
      value_ ===  TotalSales
    ) {
      myReturnArray7.push({
        SpaceID: plansInfo?.spaces[key].spaceNumber,
        TotalSales: TotalSales.toString(),
      });
    }

    if (
      operator === "UPPER_THAN" &&
      TotalSales !== undefined &&
      TotalSales !== null &&
      value_ < TotalSales
    ) {
      //console.log("found area between min and max: ", myarea);
      myReturnArray7.push({
        SpaceID: plansInfo?.spaces[key].spaceNumber,
        TotalSales: TotalSales.toString(),
      });
    }

    // we solve this  - this is only if there are two values in the area search
    if (
      operator === "LOWER_THAN" &&
      TotalSales !== undefined &&
      TotalSales !== null &&
      value_ > TotalSales
    ) {
      //console.log("found area between min and max: ", myarea);
      myReturnArray7.push({
        SpaceID: plansInfo?.spaces[key].spaceNumber,
        TotalSales: TotalSales.toString(),
      });
    }


      // 10.80.3
    if (
      operator === "BETWEEN" &&
      TotalSales !== undefined &&
      TotalSales !== null
    ) {

    value2 = value2.replace("$", "");
    value2 = value2.replace("€", "");
    value2 = value2.replace(/,/g, "");
    let value2_ = parseFloat(value2);

      if (value_ < TotalSales && value2_ > TotalSales) {

        console.log("found total sales between min and max: ", TotalSales, value_, value2_);
        myReturnArray7.push({
          SpaceID: plansInfo?.spaces[key].spaceNumber,
          TotalSales: TotalSales.toString(),
        });
      }
    }
  
  }

  return myReturnArray7;
}


// 10.80.7




/**
 * Process AI response for total rent search results
 *
 * @param field The field to search
 * @param operator The operator to use for the search
 * @param value The value for the total rent  search
 * @param value2 The value for the total rent search
 * @param result The result object to populate with search results
 * @param plansInfo The structure containing information about the spaces
 */
export function aiSearchCallbackV2_totalrent(
  field: any,
  operator: any,
  value: any,
  value2: any,
  result: NewSearchResult,
  plansInfo: any
): any {
  let myReturnArray6: Array<{
    SpaceID: string;
    TotalRent: string;
  }> = [];

  value = value.replace("$", "");
  value = value.replace("€", "");
  value = value.replace(/,/g, "");
  let value_ = parseFloat(value);
  // scale to per square foot
  //value_ = value_ / 10.764; // convert to square feet

  console.log("value_: ", value_);

  for (var key in plansInfo?.spaces ?? []) {
    let TotalRent = plansInfo?.spaces[key].detailTabs[0].detail["Total Rent"];

    if (TotalRent === undefined || TotalRent === null || TotalRent === "")
      continue;

    TotalRent = TotalRent.replace("$", "");
    TotalRent = TotalRent.replace("€", "");
    TotalRent = TotalRent.replace(/,/g, "");
    TotalRent = parseFloat(TotalRent);

    console.log("value_: ", value_, " TotalRent: ", TotalRent, " operator: ", operator);

    //TotalRentPSF = parseFloat(TotalRentPSF.replace("$", "").replace(/,/g, ""))
    //console.log("value_: ", value_, " TotalRentPSF: ", TotalRentPSF);

    // we solve this  - this is only if there are two values in the area search

    if (
      operator === "EQUAL" &&
      TotalRent !== undefined &&
      TotalRent !== null &&
      value_ ===  TotalRent
    ) {
      myReturnArray6.push({
        SpaceID: plansInfo?.spaces[key].spaceNumber,
        TotalRent: TotalRent.toString(),
      });
    }

    if (
      operator === "UPPER_THAN" &&
      TotalRent !== undefined &&
      TotalRent !== null &&
      value_ < TotalRent
    ) {
      //console.log("found area between min and max: ", myarea);
      myReturnArray6.push({
        SpaceID: plansInfo?.spaces[key].spaceNumber,
        TotalRent: TotalRent.toString(),
      });
    }

    // we solve this  - this is only if there are two values in the area search
    if (
      operator === "LOWER_THAN" &&
      TotalRent !== undefined &&
      TotalRent !== null &&
      value_ > TotalRent
    ) {
      //console.log("found area between min and max: ", myarea);
      myReturnArray6.push({
        SpaceID: plansInfo?.spaces[key].spaceNumber,
        TotalRent: TotalRent.toString(),
      });
    }


      // 10.80.3
    if (
      operator === "BETWEEN" &&
      TotalRent !== undefined &&
      TotalRent !== null
    ) {

    value2 = value2.replace("$", "");
    value2 = value2.replace("€", "");
    value2 = value2.replace(/,/g, "");
    let value2_ = parseFloat(value2);

      if (value_ < TotalRent && value2_ > TotalRent) {

        console.log("found total rent between min and max: ", TotalRent, value_, value2_);
        myReturnArray6.push({
          SpaceID: plansInfo?.spaces[key].spaceNumber,
          TotalRent: TotalRent.toString(),
        });
      }
    }
  
  }

  return myReturnArray6;
}

/**
 * Process AI response for lease end date search results
 *
 * @param field The field to search
 * @param operator The operator to use for the search
 * @param value The value for the lease end date search
 * @param value2 The value for the lease end date search (if applicable)
 * @param result The result object to populate with search results
 * @param plansInfo The structure containing information about the spaces
 */
export function aiSearchCallbackV2_leaseenddate(
  field: any,
  operator: any,
  value: any,
  value2: any,
  result: NewSearchResult,
  plansInfo: any
): any {
  let myReturnArray5: Array<{
    SpaceID: string;
    LeaseEndDate: string;
  }> = [];

  console.log(
    "aiSearchCallbackV2_leaseenddate called with field: ",
    field,
    " operator: ",
    operator,
    " value: ",
    value,
    " value2: ",
    value2
  );

  //console.log("LeaseEndDate: ", LeaseEndDate);
  // we need to convert the value to a comparable format
  if (value === undefined || value === null || value === "") {
  } else {
    value = value.replace(" ", "");
    value = value.replace("T", " ");
    value = value.replace("Z", " ");
    value = value.replace(":", "/");
    value = value.replace(/-/g, "/");
  }

  if (value2 === undefined || value2 === null || value2 === "") {
  } else {
    value2 = value2.replace(" ", "");
    value2 = value2.replace("T", " ");
    value2 = value2.replace("Z", " ");
    value2 = value2.replace(":", "/");
    value2 = value2.replace(/-/g, "/");
  }

  console.log("value: ", value, " value2: ", value2);

  const valueDate = new Date(value);
  const value2Date = new Date(value2);

  const valueTime = valueDate.getTime();
  const value2Time = value2Date.getTime();

  console.log("valueTime: ", valueTime, " value2Time: ", value2Time);

  for (var key in plansInfo?.spaces ?? []) {
    let LeaseEndDate =
      plansInfo?.spaces[key].detailTabs[0].detail["Lease End Date"];

    // I need to compare the dates, so I need to convert them to a comparable format
    if (
      LeaseEndDate === undefined ||
      LeaseEndDate === null ||
      LeaseEndDate === ""
    )
      continue;
    let LeaseEndDateOrg = LeaseEndDate;
    LeaseEndDate = LeaseEndDate.replace(" ", "");
    LeaseEndDate = LeaseEndDate.replace("T", " ");
    LeaseEndDate = LeaseEndDate.replace("Z", " ");
    LeaseEndDate = LeaseEndDate.replace(":", "/");
    LeaseEndDate = LeaseEndDate.replace(/-/g, "/");

    const leaseDate = new Date(LeaseEndDate);
    const leaseTime = leaseDate.getTime();

    console.log(
      "value: ",
      value,
      "value2: ",
      value2,
      " LeaseEndDate: ",
      LeaseEndDate,
      " leaseTime: ",
      leaseTime,
      " valueTime: ",
      valueTime,
      " value2Time: ",
      value2Time
    );

    // 
    if (
      (operator === "EQUAL") &&
      LeaseEndDate !== undefined &&
      LeaseEndDate !== null &&
      leaseTime == valueTime
    ) {
      //console.log("found area between min and max: ", myarea);
      myReturnArray5.push({
        SpaceID: plansInfo?.spaces[key].spaceNumber,
        LeaseEndDate: LeaseEndDateOrg,
      });
    }



    // we solve this  - this is only if there are two values in the area search
    if (
      (operator === "LOWER_THAN") &&
      LeaseEndDate !== undefined &&
      LeaseEndDate !== null &&
      leaseTime <= valueTime
    ) {
      //console.log("found area between min and max: ", myarea);
      myReturnArray5.push({
        SpaceID: plansInfo?.spaces[key].spaceNumber,
        LeaseEndDate: LeaseEndDateOrg,
      });
    }

    // we solve this  - this is only if there are two values in the area search
    if (
      operator === "UPPER_THAN" &&
      LeaseEndDate !== undefined &&
      LeaseEndDate !== null &&
      leaseTime >= valueTime
    ) {
      //console.log("found area between min and max: ", myarea);
      myReturnArray5.push({
        SpaceID: plansInfo?.spaces[key].spaceNumber,
        LeaseEndDate: LeaseEndDateOrg,
      });
    }

    // we solve this  - this is only if there are two values in the area search
    if (
      operator === "BETWEEN" &&
      LeaseEndDate !== undefined &&
      LeaseEndDate !== null &&
      leaseTime < value2Time &&
      leaseTime > valueTime
    ) {
      //console.log("found area between min and max: ", myarea);
      myReturnArray5.push({
        SpaceID: plansInfo?.spaces[key].spaceNumber,
        LeaseEndDate: LeaseEndDateOrg,
      });
    }
  }

  return myReturnArray5;
}
